import { ChevronRightRounded, CloseRounded } from '@mui/icons-material';
import { Box, CircularProgress, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { formatInTimeZone } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { applyFixedToDeliveries, DeliveriesCache } from 'src/components/dispatch/applyFixedToDeliveries';
import { buildPlanningEntity } from 'src/components/dispatch/autoDispatch/buildPlanningEntity';
import { resolveAutoDispatchAvailability } from 'src/components/dispatch/autoDispatch/resolveAutoDispatchAvailability';
import { useAutoDispatchController } from 'src/components/dispatch/autoDispatch/useAutoDispatchController';
import { usePlanningDialogSettings } from 'src/components/dispatch/autoDispatch/usePlanningDialogSettings';
import { resolveAutoDispatchSelectionReason } from 'src/components/dispatch/autoDispatchSelection';
import { buildMoveTargets } from 'src/components/dispatch/buildMoveTargets';
import { buildNotAllocReasonsByOrderId } from 'src/components/dispatch/buildNotAllocReasonsByOrderId';
import { DetailPanelMoveTarget, DetailPanelShell } from 'src/components/dispatch/detailPanel/DetailPanelShell';
import { DispatchSelection } from 'src/components/dispatch/detailPanel/DispatchDetailPanel';
import { SharedDestinationPanel } from 'src/components/dispatch/detailPanel/SharedDestinationPanel';
import { SharedDestinationEntryVM } from 'src/components/dispatch/detailPanel/sharedDestinationVM';
import { allRowsExpanded, collectExpandableRowIds } from 'src/components/dispatch/expandAllRows';
import { buildMoveTargetRows } from 'src/components/dispatch/moveTargetRows';
import { resolveOperationMailAvailability } from 'src/components/dispatch/operationMailAvailability';
import PanelResizeHandle from 'src/components/dispatch/panelResize/PanelResizeHandle';
import {
  fitPanelWidths, isPanelWidth, reservedWidthFor,
} from 'src/components/dispatch/panelResize/panelWidth';
import { useOrderAttrFilter } from 'src/components/dispatch/useOrderAttrFilter';
import { parseUnassignedIds } from 'src/components/dispatch/parseUnassignedIds';
import { resolvePrintTargetShiftIds } from 'src/components/dispatch/printTargets';
import { resolveProgressToastLabel } from 'src/components/dispatch/progressToastLabel';
import { collectResetTargets } from 'src/components/dispatch/resetTargets';
import { GetBusyGuardReason, resolveBusyGuardReason } from 'src/components/dispatch/resolveBusyGuardReason';
import { allRowsSelected, collectSelectableRowIds, pruneSelectedRowIds } from 'src/components/dispatch/selectableRowIds';
import { allSelectedShiftsHaveNoPlan, collectSelectedDeliveryIdsForPlanning } from 'src/components/dispatch/selectedShiftTargets';
import { buildShiftTruckRowInfoMap } from 'src/components/dispatch/shiftTruckRowInfo';
import { resolveSwapBlockReason } from 'src/components/dispatch/swapGuard';
import TariffCalculationDialog from 'src/components/dispatch/TariffCalculationDialog';
import { buildTruckColorMap } from 'src/components/dispatch/truckColorMap';
import { useDisplayMetrics } from 'src/components/dispatch/unassignedDisplay/useDisplayMetrics';
import { buildMapScenarioFilter } from 'src/components/dispatch/unassignedFilters/scenarioFilter';
import { MapScenarioFilterV1 } from 'src/components/dispatch/unassignedFilters/types';
import { useApplyResetToCaches } from 'src/components/dispatch/useApplyResetToCaches';
import { useScrollToRow } from 'src/components/dispatch/useScrollToRow';
import { useTariffCalculationAction } from 'src/components/dispatch/useTariffCalculationAction';
import { useTruckGroupSelection } from 'src/components/dispatch/useTruckGroupSelection';
import { hideDeletingShifts } from 'src/components/dispatch/vehicleAssignment/hideDeletingShifts';
import { useShiftDeletion } from 'src/components/dispatch/vehicleAssignment/useShiftDeletion';
import { useVehicleAssignmentEditing } from 'src/components/dispatch/vehicleAssignment/useVehicleAssignmentEditing';
import { VehicleAssignmentMenus } from 'src/components/dispatch/vehicleAssignment/VehicleAssignmentMenus';
import { applyVehicleOverrides, useVehicleDisplayOverrides } from 'src/components/dispatch/vehicleAssignment/vehicleDisplayOverrides';
import { buildConstraintAlertsByRowId } from 'src/components/dispatch/vehicleConstraintAlerts';
import {
  buildTruckGroupsWithAll,
  ALL_ROTATION_FILTERS,
  filterVehicleRowGroups,
  RotationFilterValue,
  VehicleFilterMode,
} from 'src/components/dispatch/vehicleFilter';
import { buildPeakLoadByRowId } from 'src/components/dispatch/vehicleLoad';
import { ExpandedStopVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { CycleIndexMap, buildCycleReorderPayload, buildReorderPlanningEntity } from 'src/components/dispatch/vehicleRowExpand/buildReorderPlanning';
import { reorderRowCardsByOperationIds } from 'src/components/dispatch/vehicleRowExpand/reorderRowCards';
import { buildSortedOperationIds } from 'src/components/dispatch/vehicleRowExpand/sortedOperationIds';
import { useDebouncedSave } from 'src/components/dispatch/vehicleRowExpand/useDebouncedSave';
import { useReportPrinting } from 'src/components/ReportPrinting';
import ReportPrintingDialogs from 'src/components/ReportPrinting/ReportPrintingDialogs';
import { appBarHeight } from 'src/constants/layout';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { DispatchCard, UnassignedItem, Vehicle } from 'src/entities/dispatch';
import { GarageEntity } from 'src/entities/Garage.entity';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { PlanningConditions } from 'src/entities/PlanningHistory.entity';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsMapGaragePositionEntity } from 'src/entities/PlanningsMapGaragePosition.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';
import { useQueryLastOperationStatusUpdated } from 'src/hooks/useQueryLastOperationStatusUpdated';
import { useMutationPlanningV5 } from 'src/hooks/useQueryPlanning';
import { useQueryPlanningGroups } from 'src/hooks/useQueryPlanningGroups';
import { useQueryPlanningsNotAllocReasons } from 'src/hooks/useQueryPlanningsNotAllocReasons';
import { useMutationPlanningOperations } from 'src/hooks/useQueryPlanningsOperations';
import { useQueryPlanningsSuggestions } from 'src/hooks/useQueryPlanningsSuggestions';
import { useMutationPlanningTruckDisplayOrders } from 'src/hooks/useQueryPlanningsTrucks';
import { useMutationShiftTruckFixedStatusV5 } from 'src/hooks/useQueryShiftTruckFixedStatus';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { useMutationShipmentOrder } from 'src/hooks/useQueryShipmentOrders';
import { useMutationShipmentOrderLegRun } from 'src/hooks/useShipmentOrderLegRun';
import { toUnassignedItem, buildVehicleRowGroups, createVehicleRowGroupsCache, RotationRow, rowIdFromTruck, VehicleRowGroup, VehicleRowGroupsCache } from 'src/pages/ConvenienceDispatches/adapter';
import { buildGrossProfitByTruckId } from 'src/pages/ConvenienceDispatches/buildGrossProfitByTruckId';
import ConvenienceDispatchAddVehicleDialog from 'src/pages/ConvenienceDispatches/ConvenienceDispatchAddVehicleDialog';
import ConvenienceDispatchUnassignedList from 'src/pages/ConvenienceDispatches/ConvenienceDispatchUnassignedList';
import ConvenienceDispatchVehicleList from 'src/pages/ConvenienceDispatches/ConvenienceDispatchVehicleList';
import DispatchDetailDialogs from 'src/pages/ConvenienceDispatches/DispatchDetailDialogs';
import {
  buildLegRunMaterialsMap,
  buildRowIdToShiftTruckId,
  resolveShipmentOrderIdsFromUnassignedIds,
} from 'src/pages/ConvenienceDispatches/rowLookup';
import { buildPlanningScopeKey, resolveSortedTruckIds } from 'src/pages/ConvenienceDispatches/truckOrder';
import { MoveTarget, useDispatchDetailActions } from 'src/pages/ConvenienceDispatches/useDispatchDetailActions';
import { useRotationEditing } from 'src/pages/ConvenienceDispatches/useRotationEditing';
import { buildVehicleRows } from 'src/pages/ConvenienceDispatches/vehicleRowVM';
import NotAllocSuggestionComponent from 'src/pages/V3Plans/components/NotAllocSuggestion.component';
import PlanningPresenter from 'src/pages/V3Plans/presenters/PlanningPresenter';
import { ScenarioPlanningListDialog } from 'src/pages/V3Plans/presenters/ScenarioPlanningListDialog';
import { compareTrucksByDisplayOrder } from 'src/utils/truckDisplayOrder.util';
import { RelaxedRuleValues, RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import { buildCyclesFromDelivery } from './buildCyclesFromDelivery';
import { buildExpandedRowVMByRowId } from './buildExpandedRowVMByRowId';
import { buildMapPlanningSetting } from './buildMapPlanningSetting';
import { buildDeliveryOperations, buildSelectedCycleIndexes } from './buildPlanningRequestParts';
import { buildSharedDestinationVM } from './buildSharedDestinationVM';
import { buildVehicleExtraMap } from './buildVehicleExtraMap';
import { GroupedPinContext } from './clusterEvents';
import { buildDeliveryPlanWindows } from './deliveryPlanWindows';
import { buildDeliveryTerminals } from './deliveryTerminals';
import { resolveSelectionFromStop } from './detailPanelWiring';
import {
  buildDayByShiftTruckId, buildDisplayDays, filterLegRunsByDay, filterRowGroupsByDay,
} from './displayDays';
import DisplayDayTabs from './DisplayDayTabs';
import { collectSelectedShiftTruckIds, decideNextFixed, areAllFixed } from './fixedToggle';
import { buildMapAutoDispatchHooks } from './mapAutoDispatchHooks';
import { MapDisplayFilter } from './MapDisplayFilter';
import { DISPLAY_KIND_OPTIONS, isDisplayKindArray } from './mapDisplayKinds';
import MapToolbar from './MapToolbar';
import { buildMapUnassignedMarkers, buildMapVehicleRoutes, MapRouteMarkerVM } from './mapVM';
import {
  DEFAULT_LEFT_PANEL_WIDTH, DEFAULT_RIGHT_PANEL_WIDTH,
  LEFT_PANEL_WIDTH_STORAGE_KEY, RIGHT_PANEL_WIDTH_STORAGE_KEY,
} from './panelWidth';
import {
  filterHiddenUnassigned, pruneHiddenUnassignedIds, revealUnassignedIds, unassignedIdsOfLegRuns,
} from './planningHiddenUnassigned';
import MapView from './presenters/MapView.presenter';
import { VehicleColorPickerPopover } from './presenters/VehicleColorPickerPopover';
import { pruneSelectedUnassignedIds } from './pruneSelection';
import { resolveGarageByDeliveryId } from './resolveGarageName';
import { resolveMapTariffAvailability } from './resolveMapTariffAvailability';
import { collectRowIdsAtPlace } from './rowIdsAtPlace';
import { buildUnassignedPolylines } from './unassignedPolylines';
import { buildUnassignedTooltipMap } from './unassignedTooltip';
import { useExpandedRowIds } from './useExpandedRowIds';
import { useNewlyAssignedHighlight } from './useNewlyAssignedHighlight';
import { useOperationStatusFreshness } from './useOperationStatusFreshness';
import { useUnassignedOverride } from './useUnassignedOverride';
import { resolveVehicleColor } from './vehicleColorPalette';

type Props = {
  deliveries: PlanningsDeliveryWithLegRunEntity[];
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  trucks: PlanningsTruckEntity[];
  drivers: PlanningsDriverEntity[];
  garageData: GarageEntity[];
  isLoading: boolean;
  /**
   * 表示日。取得・キャッシュ操作・自動配車の対象がすべてこの 1 日に揃う
   * （Container が期間から選んで渡す。startOn === endOn）。
   */
  startOn: string;
  endOn: string;
  /** URL の期間。表示日タブと日付ピッカーだけがこれを見る */
  rangeStartOn: string;
  rangeEndOn: string;
  /** 表示日タブで日を選んだときに呼ぶ。取得しなおすのは Container の責務 */
  onDisplayDayChange: (day: string) => void;
  /** 未割当リストの32列フィルタへ素通しする値。ConvenienceDispatches/Presenter.tsx と同じ契約 */
  customInputFieldNames: string[];
  trucksForFilter: { id: number; name: string }[];
  driversForFilter: { id: number; name: string }[];
  companyId: string;
  /** Task 3（可否判定）・Task 7（シナリオボタン描画）が使う。companyId から導出しないこと
   * （companyId はライセンス未取得中 '0' になり全社選択と区別できないため） */
  useAlgorithmPlanning: boolean;
  /** license.config.load_after_initial_stock。並べ替え保存の計算条件（V3Plans Component.tsx:903） */
  loadAfterInitialStock: boolean;
  /** license.config.set_current_time_to_algorithm。同上（V3Plans Component.tsx:904） */
  setCurrentTimeToAlgorithm: boolean;
  useScenarioPlanning: boolean;
  /**
   * license.config.delivery_list_grouping（表示設定 > 配送リストの地点表示）。
   * groupByName なら同一座標でも納品先名称が違う地点を分けて出す。
   * 旧地図配車・タイムライン・幹線輸送は反映済みで、新地図だけ見ていなかった
   * （依頼 log_web_issues#299-2）。
   */
  deliveryListGrouping?: DeliveryListGroupingValues;
  selectedCompanyId: number | null;
  isLicenseReady: boolean;
  /** license.config.use_driver_app。未取得なら undefined。配車指示メールの表示条件 */
  useDriverApp?: boolean;
  /** license.config.use_truck_position。運行状況表示の表示条件 */
  useTruckPosition?: boolean;
  isMastersReady: boolean;
  /** マスタ取得が失敗したか。Container は必ず渡せるので必須にして伝播漏れを tsc に検出させる */
  isMastersError: boolean;
  /**
   * 配車データの取得系だけを見た「運賃計算できない理由」。null なら取得は正常。
   * Presenter は query を持たないため Container 側で導出して渡す
   * (resolveDeliveryDataDisabledReason)。必須にして伝播漏れを tsc に検出させる。
   */
  deliveryDataDisabledReason: string | null;
  /**
   * deliveries にまだ続きのページがあるか。1 ページずつ届く途中で車両グループ一覧を
   * 組み立てると、車両が増えるたびに一覧が変わって選択 index の指す先がずれる。
   * V3Plans (Component.tsx:368-383) と同じく、揃うまで待つために使う。
   */
  deliveriesHasNextPage?: boolean;
  /** 画面遷移。ツールバーの導線が使う */
  onNavigate: (path: string) => void;
  /**
   * ヘッダの日付範囲ピッカーが確定したときに呼ぶ。`/map` は日付を URL に持つので
   * Container が `navigate('/map/{startOn}/{endOn}')` する（`/plans` と同じ方式）。
   * 履歴に残す（`replace` しない）ので戻るボタンで前の日付へ戻れる。
   */
  onDateRangeChange: (startOn: string, endOn: string) => void;
};

// 回転追加/削除(P1d)は実ハンドラ(handleAddRotation/handleDeleteRotation)を配線済み。

// P0 は車両フィルタ機能が無いため常に「すべて表示」固定。明示的に型注釈し、

/**
 * 同じ対象を指しているか (クリック入口のトグル判定)。
 * カード配車は resolveCardClick が選択カードと詳細をまとめて決めるが、/map には
 * 選択カードの概念が無いので詳細の開閉だけを見る。
 * card.id だけでは同一地点への再訪 (同じカードを別の順番で訪れる) を区別できないため
 * orderIndex も併せて比較する。
 *
 * Why not: unassigned 同士の比較は持たない。この関数を使う openDetailFromClick の
 * 呼び出し元は停車地クリックと地図マーカークリックの 2 つだけで、next は必ず 'vehicle'
 * か null になる。未割当ピンのトグルはピン留め id (pinnedUnassignedIdRef) で判定する。
 */
function isSameDetailTarget(current: DispatchSelection, next: DispatchSelection): boolean {
  if (!current || !next) return false;
  if (current.source === 'vehicle' && next.source === 'vehicle') {
    return current.card.id === next.card.id && current.orderIndex === next.orderIndex;
  }
  return false;
}

/** 並べ替えの保存に送るもの。回転を動かしたときだけ回転番号が付く */
type ReorderSavePayload = {
  deliveryId: number;
  sortedOperationIds: number[];
  orderOperationCycleIndexes?: CycleIndexMap;
};

const MapPagePresenter = ({
  deliveries,
  legRuns,
  trucks,
  drivers,
  garageData,
  isLoading,
  startOn,
  endOn,
  rangeStartOn,
  rangeEndOn,
  onDisplayDayChange,
  customInputFieldNames,
  trucksForFilter,
  driversForFilter,
  companyId,
  useAlgorithmPlanning,
  loadAfterInitialStock,
  setCurrentTimeToAlgorithm,
  useScenarioPlanning,
  deliveryListGrouping = 'groupByAddress',
  selectedCompanyId,
  isLicenseReady,
  useDriverApp,
  useTruckPosition,
  isMastersReady,
  isMastersError,
  deliveryDataDisabledReason,
  deliveriesHasNextPage = false,
  onNavigate,
  onDateRangeChange,
}: Props) => {
  // scope（会社・対象日）キー。並び順の楽観 state・回転編集のリセット判定で共有する。
  // 生成はキー生成関数 1 箇所 (buildPlanningScopeKey) に寄せ、会社を切り替えても
  // 前会社の並び順・追加回転が残らないようにする (ConvenienceDispatches/Presenter.tsx と同じ規則)。
  const scopeKey = buildPlanningScopeKey(companyId, startOn, endOn);

  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());

  /**
   * 表示日。期間を跨いで開くと左右のリストに全期間ぶんが混ざり、どれがいつのデータか
   * 分からなかった（指摘 2026/08/18）。1 日ぶんに絞って見せる。
   *
   * 選んでいる日は Container が startOn として渡す（取得も同じ日に絞るため）。
   * 1 日だけの期間ではタブを出さないので、絞り込みも掛けない（selectedDay は null）。
   * 取得済みのデータにも前日から続く勤務が混ざりうるので、絞り込み自体は残す。
   */
  const displayDays = useMemo(() => buildDisplayDays(rangeStartOn, rangeEndOn), [rangeStartOn, rangeEndOn]);
  const selectedDay = displayDays.length <= 1 ? null : startOn;
  const dayByShiftTruckId = useMemo(
    () => buildDayByShiftTruckId(deliveries, displayDays),
    [deliveries, displayDays],
  );

  const unassignedForVM = useMemo(() => legRuns.filter((lr) => !lr.assigned), [legRuns]);

  // `/plannings/.../deliveries` の `shipmentOrderLegRuns[]` は compact=true でシリアライズ
  // されており、数量 / 備考 / 作業秒 / 指定時間 などが抜けている。
  // 一方 `/api/v5/shipment_order_leg_runs` は非 compact なので、id で突き合わせて補完する。
  // (ConvenienceDispatches/Presenter.tsx の enrichedDeliveries と同じ手順・同じ helper)
  const legRunMaterialsMap = useMemo(() => buildLegRunMaterialsMap(legRuns), [legRuns]);

  // deliveries のコピーを作って legRun を補完してから buildVehicleRows に渡す
  const enrichedDeliveries = useMemo(() => {
    if (legRunMaterialsMap.size === 0) return deliveries;
    return deliveries.map((d) => ({
      ...d,
      shipmentOrderLegRuns: d.shipmentOrderLegRuns.map((lr) => {
        if (lr.id == null) return lr;
        const rich = legRunMaterialsMap.get(lr.id);
        if (!rich) return lr;
        // 非 compact 側 (rich) に存在するフィールドで補完する。
        // delivery 由来の compact 値 (lr) を base にし、rich の値で上書き。
        return { ...lr, ...rich };
      }),
    }));
  }, [deliveries, legRunMaterialsMap]);

  // vehicleRowVM.ts の buildVehicleRows は rowGroups/routes の両方から参照されるため、
  // 1 回だけ計算して共有する (REFACTOR: 重複呼び出しの解消)。
  // useRotationEditing に渡すサーバ純粋由来行 (回転追加/削除のマージ前)。
  const sourceRowsRaw = useMemo(
    () => buildVehicleRows(enrichedDeliveries, null, unassignedForVM, trucks, drivers, deliveryListGrouping),
    [enrichedDeliveries, unassignedForVM, trucks, drivers, deliveryListGrouping],
  );

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // 汎用確認ダイアログ・resetting mutation は useRotationEditing が直接参照するため、
  // ここへ前倒しして定義する（TDZ 回避。convenience Presenter.tsx:592-601 と同じ配置規則）。
  const [confirmDialog, setConfirmDialog] = useState<{
    /** 省略すると本文だけの確認になる（旧画面の window.confirm と同じ見え方） */
    title?: string;
    description: string;
    onConfirm: () => void | Promise<void>;
  } | null>(null);

  // Task 10: 自動配車の実行中に競合する書き込みを弾く共通ガード。useRotationEditing
  // (下記) より前に定義する必要がある (TDZ 回避)。getter は ref を読むだけにして
  // 関数の同一性を保つ (毎レンダー新しい関数にすると、これを deps に持つ
  // useCallback/useEffect が連鎖して再生成される)。値の同期は controller 生成後の
  // useLayoutEffect で行う (下記)。
  const busyGuardRef = useRef<string | null>(null);
  const getBusyGuardReason = useCallback<GetBusyGuardReason>(() => busyGuardRef.current, []);

  // normal/transfer/timeline は自動配車 controller (下記) の DI 用。operationBulkResetting は
  // 従来どおり回転削除・運賃計算・詳細パネルの「未割当へ戻す」が使う。1 箇所の呼び出しから
  // 共有することで、controller 内部の reset と画面側が読む isPending を同じ instance にする
  // （理由はカード配車の同箇所コメント・ConvenienceDispatches/Presenter.tsx:1222-1227 参照）
  const {
    normalPlanning, transferPlanning, timelinePlanning, operationBulkResetting, swapPlanning,
  } = useMutationPlanningV5();
  const resettingMutateAsync = operationBulkResetting.mutateAsync;

  // 回転編集（追加/削除・仮想行マージ・placeholder 非表示）は useRotationEditing に集約。
  // getRowGroups は orderedRowGroups（可視 group・applyHiddenFilter 適用後）を遅延アローで参照する。
  // orderedRowGroups はこの hook 呼び出しより後方で定義されるが、実行時に評価されるのは
  // handleDeleteRotation 呼び出し時 (ユーザー操作時) であり、その時点で render は完了済みのため
  // TDZ にはならない (convenience Presenter.tsx:606-619 と同じ規則)。
  // インラインで渡すと毎レンダー別関数になり、useRotationEditing が返す
  // handleDeleteRotation まで作り直されて memo 済みの車両リストが再描画される
  const resetLegRunsForRotation = useCallback(async (ids: number[]) => {
      // Task 10 #2: useRotationEditing.ts は触らず、注入されるこのコールバック内で弾く。
      // Why not 黙って return: onConfirm 側が成功扱いのままになり「未割当に戻しました」
      // という誤った成功通知が出てしまう。throw して useRotationEditing の catch に処理
      // させる (自前で enqueueSnackbar は呼ばない。呼ぶと catch の通知と二重になるため)。
      // AxiosError 形にラップし、err.response?.data?.message が busyReason そのものに
      // なるようにする (useRotationEditing.ts:106 と同じ形。裸の Error だと
      // response が無いので「処理に失敗しました」という別の文言に化けてしまう)。
      const busyReason = getBusyGuardReason();
      if (busyReason) {
        throw Object.assign(new Error(busyReason), { response: { data: { message: busyReason } } });
      }
      await resettingMutateAsync(ids);
      // deliveries(配車済み) と未割当 legRuns の両方を refetch させる。prefix 部分一致 invalidate。
      void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
  }, [getBusyGuardReason, resettingMutateAsync, queryClient]);

  const notify = useCallback(
    (message: string, variant?: 'success' | 'error' | 'info') => { enqueueSnackbar(message, { variant }); },
    [enqueueSnackbar],
  );

  // インラインの `() => orderedRowGroups` だと毎レンダー別関数になり、
  // useRotationEditing が返す handleDeleteRotation まで作り直されて
  // memo 済みの車両リストが未割当の選択でも再描画される。ref 経由で参照を固定する
  const orderedRowGroupsRef = useRef<VehicleRowGroup[]>([]);
  const getOrderedRowGroups = useCallback(() => orderedRowGroupsRef.current, []);

  const {
    sourceRows,
    applyHiddenFilter,
    handleAddRotation,
    handleDeleteRotation,
  } = useRotationEditing({
    sourceRowsRaw,
    getRowGroups: getOrderedRowGroups,
    resetAllKey: scopeKey,
    // /map には convenience の固定ルートに相当するものが無いため、対象日と同値にする。
    hiddenResetKey: scopeKey,
    resetLegRuns: resetLegRunsForRotation,
    confirm: setConfirmDialog,
    notify,
  });

  /**
   * 並べ替え直後の見た目。rowId → 新しい operation の並び。
   *
   * 保存は自動配車の順序固定実行で、完了まで 9〜16 秒かかる。それまで画面が動かないと
   * 操作が効いていないように見えるので、掴んだ時点の並びを先に見せる。
   * カード列・地図のルート線・展開パネルはどれも rowGroups から作るので、
   * ここ 1 か所に効かせれば 3 つとも同時に動く。
   */
  const [stopOrderPreview, setStopOrderPreview] = useState<Map<string, number[]>>(new Map());
  // サーバの並びが返ったら捨てる（ExpandedRowPanel の preview と同じ規則）
  useEffect(() => {
    setStopOrderPreview((prev) => (prev.size === 0 ? prev : new Map()));
  }, [deliveries]);

  const previewedSourceRows = useMemo(() => {
    if (stopOrderPreview.size === 0) return sourceRows;
    return sourceRows.map((vm) => {
      const operationIds = stopOrderPreview.get(rowIdFromTruck(vm.truckId, vm.rotationNumber, vm.deliveryId));
      if (!operationIds) return vm;
      const cards = reorderRowCardsByOperationIds(vm.cards, operationIds);
      return cards === vm.cards ? vm : { ...vm, cards };
    });
  }, [sourceRows, stopOrderPreview]);

  // 勤務ごとのドライバー名の解決に使う（車両の代表情報は truckId 単位なので、
  // 1 台に複数の勤務が乗るときは行の driverId から引く）
  const driverNameById = useMemo(() => new Map(drivers.map((d) => [d.id, d.name])), [drivers]);

  // Why not rowGroups の useMemo の中で組む: previewedSourceRows が変わるたびに extraMap も
  // 新しい Map になり、buildVehicleRowGroups の再利用条件（補助 Map が同一参照）が
  // 一度も成立しない。引数 3 つの純関数で、その 3 つは元から同じ memo の依存に
  // 入っていたので分離しても結果は変わらない
  const extraMap = useMemo(
    () => buildVehicleExtraMap(deliveries, trucks, drivers),
    [deliveries, trucks, drivers],
  );
  // 前回の結果を持ち回して、変わっていないグループは同じオブジェクトを返す
  const rowGroupsCacheRef = useRef<VehicleRowGroupsCache | null>(null);
  rowGroupsCacheRef.current ??= createVehicleRowGroupsCache();
  const rowGroupsCache = rowGroupsCacheRef.current;
  const rowGroups = useMemo(
    () => buildVehicleRowGroups(previewedSourceRows, extraMap, driverNameById, rowGroupsCache),
    [previewedSourceRows, extraMap, driverNameById, rowGroupsCache],
  );

  const derivedUnassignedItems = useMemo(
    // 表示日で絞ってから item にする。地図のピン・選択の整理・件数もここから派生する
    () => filterLegRunsByDay(unassignedForVM, selectedDay, displayDays)
      .map((lr, i) => toUnassignedItem(lr, i)),
    [unassignedForVM, selectedDay, displayDays],
  );

  const [selectedUnassignedIds, setSelectedUnassignedIds] = useState<Set<string>>(new Set());

  // 削除の楽観更新。/map の未割当は useMemo 派生で setter が無いため、配列を上書きせず
  // 未確定の削除操作から表示を導出する (useUnassignedOverride 参照)。
  const {
    items: unassignedItems,
    optimistic: unassignedOptimistic,
    hasPendingRemoval,
  } = useUnassignedOverride({
    derivedItems: derivedUnassignedItems,
    scopeKey,
    selectedIds: selectedUnassignedIds,
    setSelectedIds: setSelectedUnassignedIds,
  });

  /**
   * 自動配車に送った未割当。応答が返るまでリストと地図から消す。
   * 残ったままだと計算中に「まだ未割当」なのか「もう割り付いた」のかが読めない。
   * 選択は解除しない（割り付かなかった案件が選択状態のまま戻る）。
   */
  const [planningHiddenIds, setPlanningHiddenIds] = useState<ReadonlySet<string>>(new Set());
  const hideUnassignedForPlanning = useCallback((legRunIds: number[]) => {
    setPlanningHiddenIds(unassignedIdsOfLegRuns(legRunIds));
  }, []);
  const revealUnassignedForPlanning = useCallback((legRunIds: number[]) => {
    setPlanningHiddenIds((prev) => revealUnassignedIds(prev, legRunIds));
  }, []);
  /**
   * 停止したとき。その場で全部戻す。
   *
   * 停止は「1 件も割り付いていない」ことが確定しているので、下の失敗時のように
   * 未割当一覧の取り直しを待つ理由が無い。待つと前方一致で「訪問した全日付 ×
   * 全ページ」の再取得が終わるまで復帰が遅れ、カード配車（onCancelled で
   * ローカル state を戻すだけ）より明らかに遅い (log_web_issues#297)。
   * データの最新化は controller の refreshServerDeliveries が非同期に撃っている。
   */
  const revealAllUnassignedNow = useCallback(() => {
    setPlanningHiddenIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);
  /**
   * 失敗したとき。失敗しても「1 件も割り付いていない」とは限らない。controller は
   * 失敗時も「サーバ上でジョブがまだ実行中の可能性がある」として未割当一覧を取り直す
   * (useAutoDispatchController.ts:489-494)。先に全部戻すと、割り付いた案件が
   * 一瞬だけ未割当に戻って見える。取り直してから戻す。
   */
  const revealAllUnassignedAfterRefetch = useCallback(() => {
    void queryClient
      .refetchQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] })
      .finally(() => setPlanningHiddenIds((prev) => (prev.size === 0 ? prev : new Set())));
  }, [queryClient]);

  // 割り付いてリストから消えたら追跡をやめる。残すと、配車を取り消して未割当へ戻したとき
  // 隠れたままになる
  useEffect(() => {
    setPlanningHiddenIds((prev) => pruneHiddenUnassignedIds(prev, unassignedItems));
  }, [unassignedItems]);

  // 表示用。選択の整理 (pruneSelectedUnassignedIds) は unassignedItems を見るので、
  // ここで隠しても選択は保持される
  const visibleUnassignedItems = useMemo(
    () => filterHiddenUnassigned(unassignedItems, planningHiddenIds),
    [unassignedItems, planningHiddenIds],
  );

  // 地図のピンも楽観削除に追従させる。派生値のまま描くと、消えたはずの案件のピンが
  // 応答待ちの間だけ残り、クリックすると削除済みの詳細が開いてしまう。
  //
  // ピンは地点ごとに 1 本で、案件は ids にぶら下がる (log_web_issues#2430) ので、
  // 絞り込みは ids に対して行う。同じ地点の一部だけ消えたときはピンを残し、
  // 全部消えたときだけピンごと落とす。代表 id が消えたら残った先頭へ付け替える
  // (代表 id はツールチップの引き当てキーなので、消えた id のままだとホバーが空になる)。
  const unassignedMarkers = useMemo(() => {
    const markers = buildMapUnassignedMarkers(unassignedForVM);
    if (visibleUnassignedItems === derivedUnassignedItems) return markers;
    const visibleIds = new Set(visibleUnassignedItems.map((item) => item.id));
    return markers.flatMap((m) => {
      const ids = m.ids.filter((id) => visibleIds.has(id));
      if (ids.length === 0) return [];
      return [{ ...m, id: ids[0], ids }];
    });
  }, [unassignedForVM, visibleUnassignedItems, derivedUnassignedItems]);

  const onSelectUnassigned = useCallback((id: string) => {
    setSelectedUnassignedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    setSelectedUnassignedIds((prev) => pruneSelectedUnassignedIds(prev, unassignedItems));
  }, [unassignedItems]);

  const { restoreSplitLegRun } = useMutationShipmentOrderLegRun();
  // useMutation の戻り値は毎レンダー新しいオブジェクト。mutate だけを取り出して依存にする。
  // 丸ごと依存にすると下のハンドラも毎回作り直され、memo 済みの未割当リストが
  // 車両を選ぶたびに再描画される（実測で確認）
  const restoreSplitMutate = restoreSplitLegRun.mutate;

  // インラインの arrow を渡すと毎レンダー別関数になり、memo 済みの未割当リストが
  // 車両を選ぶたびに再描画される
  const openTariffDialog = useCallback(() => setTariffDialogOpen(true), []);

  const handleRestoreSplit = useCallback((item: UnassignedItem) => {
    const legRunId = item.id.startsWith('lr-') ? parseInt(item.id.slice(3), 10) : NaN;
    if (Number.isNaN(legRunId)) return;
    setConfirmDialog({
      title: '分割した案件を復元しますか？',
      description: '分割した案件を元に戻します。よろしいですか？',
      onConfirm: () => {
        // Task 10 #3: ダイアログを開いた後に busy になった場合の保険 (設計書 :498-512)。
        const busyReason = getBusyGuardReason();
        if (busyReason) {
          enqueueSnackbar(busyReason, { variant: 'error' });
          return;
        }
        restoreSplitMutate(legRunId, {
          onSuccess: () => {
            // 再取得は mutation の onSuccess が invalidateQueries するのに任せる
            // （Presenter は query オブジェクトを持たない。refetch prop は作らない）。
            enqueueSnackbar('分割した案件を元に戻しました', { variant: 'success' });
          },
          onError: () => {
            enqueueSnackbar('分割した案件を元に戻す処理でエラーが発生しました', { variant: 'error' });
          },
        });
      },
    });
  }, [restoreSplitMutate, enqueueSnackbar, getBusyGuardReason]);

  // 車庫は /plans と同じ表示（GarageEntity 由来の実名＋ピン）にするため、
  // deliveries の車庫座標を GarageEntity と突合して PlanningsMapGaragePositionEntity を組む
  // （V3Plans/Component.tsx の planningsMapGaragePositionEntities と同じロジック）。
  const garageEntities = useMemo((): PlanningsMapGaragePositionEntity[] => {
    if (deliveries.length === 0 || garageData.length === 0) return [];
    const seen = new Map<string, PlanningsMapGaragePositionEntity>();
    for (const d of deliveries) {
      if (!d.garageLatitude || !d.garageLongitude) continue;
      const key = `${d.garageLatitude},${d.garageLongitude}`;
      let entity = seen.get(key);
      if (!entity) {
        const garage = garageData.find(
          (it) => it.latitude === d.garageLatitude && it.longitude === d.garageLongitude,
        );
        if (!garage) continue;
        entity = {
          garageEntity: garage,
          position: [parseFloat(d.garageLatitude), parseFloat(d.garageLongitude)],
          deliveryIds: [],
        };
        seen.set(key, entity);
      }
      entity.deliveryIds.push(d.id);
    }
    return [...seen.values()];
  }, [deliveries, garageData]);

  // 実効 Set (displayMetricSet) は useDisplayMetrics が effectiveDisplayMetrics を通して
  // 導出する (契約: 数値指標は常に最低 1 つ有効)。Presenter 側で導出し直さないのは、
  // 呼び忘れで未割当カードだけ実効化されない不具合を再発させないため。
  const [displayMetrics, setDisplayMetrics, displayMetricSet, vehicleDisplayMetricSet] = useDisplayMetrics();

  // 車両リストの props / VM は運賃を持たないため、粗利は Presenter で算出して map で渡す。
  const grossProfitByTruckId = useMemo(() => buildGrossProfitByTruckId(deliveries), [deliveries]);

  // 確定状態は delivery.fixed（= shift_truck.shift.fixed）。shift_truck_id = delivery.id。
  const fixedShiftTruckIds = useMemo(
    () => new Set(deliveries.filter((d) => d.fixed).map((d) => d.id)),
    [deliveries],
  );

  const fixedStatusMutation = useMutationShiftTruckFixedStatusV5();
  // useMutation の戻り値は毎レンダー新しいオブジェクト。mutate 自体は安定なので、
  // 依存にはこちらを使う（オブジェクトを依存にすると memo 済みの子が毎回作り直される）
  const fixedStatusMutate = fixedStatusMutation.mutate;

  const handleToggleFixedSelected = useCallback(() => {
    const shiftTruckIds = collectSelectedShiftTruckIds(selectedRowIds, rowGroups);
    if (shiftTruckIds.length === 0) return;
    // Task 10 #1: disabled は押す前の抑止。確認を経ない操作なので、mutate 直前でも
    // 開いていた画面からの発火を弾く (設計書 :498-512 の保険層)。
    const busyReason = getBusyGuardReason();
    if (busyReason) {
      enqueueSnackbar(busyReason, { variant: 'error' });
      return;
    }

    const nextFixed = decideNextFixed(shiftTruckIds, fixedShiftTruckIds);

    // API を待つと反映まで 9〜16 秒かかり、押しても 🔒確定 が変わらない。
    // 先にキャッシュを書き換え、失敗したら戻す（車両/車番/ドライバーの楽観表示と同型）。
    const fixedKeys = [
      [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY],
      [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY],
    ];
    // deliveries は infinite (= { pages }) と素の配列の 2 形式がある。
    // applyFixedToDeliveries が両方を見分けるので、ここでは型を固定しない
    const rollbacks = fixedKeys.map((key) => {
      const before = queryClient.getQueriesData({ queryKey: key });
      queryClient.setQueriesData<DeliveriesCache>(
        { queryKey: key },
        (old) => applyFixedToDeliveries(old, shiftTruckIds, nextFixed),
      );
      return before;
    });

    fixedStatusMutate(
      { shift_truck_ids: shiftTruckIds, fixed: nextFixed },
      {
        onSuccess: () => {
          // 確定 hook の onSuccess は V3Plans キャッシュ(別 query key)を更新するため、
          // /map が読む convenience deliveries キーを invalidate して真値に揃える。
          // 楽観更新と同じ値がサーバから返れば見た目は変わらない。
          void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
          void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
        },
        onError: () => {
          rollbacks.flat().forEach(([key, data]) => queryClient.setQueryData(key, data));
          enqueueSnackbar('配車確定の更新に失敗しました', { variant: 'error' });
        },
      },
    );
  }, [selectedRowIds, rowGroups, fixedShiftTruckIds, fixedStatusMutate, queryClient, enqueueSnackbar, getBusyGuardReason]);

  // 確定ボタンのラベル（対象ゼロなら undefined ＝ ボタン非表示）。
  // collectSelectedShiftTruckIds は placeholder/負値 id を除くため、placeholder 行だけ選択時は
  // length 0 → undefined となり「対象ゼロのボタン」を出さない。
  const fixedToggleLabel = useMemo(() => {
    const shiftTruckIds = collectSelectedShiftTruckIds(selectedRowIds, rowGroups);
    if (shiftTruckIds.length === 0) return undefined;
    // 件数は選択行数ではなく実際に送る shift_truck 数。同一車両の複数回転は 1 件に dedup される
    const verb = areAllFixed(shiftTruckIds, fixedShiftTruckIds) ? '確定解除' : '配車確定';
    return `${verb} (${shiftTruckIds.length})`;
  }, [selectedRowIds, rowGroups, fixedShiftTruckIds]);

  // 車両表示順 (ConvenienceDispatches/Presenter.tsx と同じ仕組み)。ドラッグで即座に並びを変える
  // 楽観的更新をするために、サーバ確定前の order を local state で保持する。range が変わったらリセット。
  const canSortTruckDisplayOrder = startOn === endOn;
  const truckDisplayOrderMutation = useMutationPlanningTruckDisplayOrders(startOn, endOn);
  const truckDisplayOrderMutate = truckDisplayOrderMutation.mutate;
  const [sortedTruckIdsState, setSortedTruckIdsState] = useState<{ rangeKey: string; ids: number[] }>({
    rangeKey: '',
    ids: [],
  });
  const sortedTruckIds = useMemo(
    () => resolveSortedTruckIds(sortedTruckIdsState, scopeKey),
    [scopeKey, sortedTruckIdsState],
  );

  const handleTruckOrderChange = useCallback((visibleNextIds: number[]) => {
    if (!canSortTruckDisplayOrder) return;
    // visibleNextIds はフィルタ範囲内 (/map は現状フィルタ無しなので全車両) のみの並び順。
    // API は受け取った truck_ids だけで version を再作成するため、フィルタ外の車両も含めた
    // 全車両 (trucks) を base にマージして送る (ConvenienceDispatches/Presenter.tsx の
    // handleTruckOrderChange を写経)。
    //
    // base 選定:
    //  - 既に DnD 後の楽観的 state (sortedTruckIds) があればそれを優先 (連続 drag 対応)
    //  - そうでなければ trucks マスタを API と同じ規則 (displayOrder bucket → sortOrder) で
    //    並べた順を使う
    const baseTruckIds = sortedTruckIds.length > 0
      ? sortedTruckIds
      : trucks
        .slice()
        .sort(compareTrucksByDisplayOrder)
        .map((t) => t.id);

    const visibleSet = new Set(visibleNextIds);
    let pointer = 0;
    const mergedIds = baseTruckIds.map((id) => {
      if (visibleSet.has(id) && pointer < visibleNextIds.length) {
        const next = visibleNextIds[pointer];
        pointer += 1;
        return next;
      }
      return id;
    });
    // 念のため: base にない visibleNextIds (= 新規 truck など) は末尾に append
    while (pointer < visibleNextIds.length) {
      mergedIds.push(visibleNextIds[pointer]);
      pointer += 1;
    }

    // 楽観的更新: ドラッグ直後に並びを反映する
    setSortedTruckIdsState({ rangeKey: scopeKey, ids: mergedIds });
    truckDisplayOrderMutate(mergedIds, {
      onError: () => {
        // ロールバック: rangeKey をクリアしてサーバ真値からの再構築を促す
        setSortedTruckIdsState({ rangeKey: '', ids: [] });
        enqueueSnackbar('車両表示順の保存に失敗しました', { variant: 'error' });
      },
    });
    // useMutation の戻り値は毎レンダー新しいオブジェクト。mutate は安定なのでそちらを依存にする
    // (@tanstack/react-query useMutation.js:31-36)
  }, [canSortTruckDisplayOrder, enqueueSnackbar, scopeKey, sortedTruckIds, trucks, truckDisplayOrderMutate]);

  // 楽観順の適用: sortedTruckIds の順序で rowGroups を並べ替える
  // (ConvenienceDispatches/Presenter.tsx の filteredRowGroups sort を範に、/map にはフィルタが
  // 無いため並替のみ適用する)。VehicleRowGroup.vehicle.truckId は truck マスタ id
  // (number | null) を直接持つため、convenience 版のような vehicle.id 文字列パースは不要。
  // ordering の後に applyHiddenFilter（ユーザーが×で非表示にした placeholder rotation の除外）を
  // 適用し、左リスト・地図(routes)双方が参照する「可視 group」を確定する。
  const orderedRowGroups = useMemo((): VehicleRowGroup[] => {
    const ordered = (() => {
      if (sortedTruckIds.length === 0) return rowGroups;
      const orderIndex = new Map(sortedTruckIds.map((id, idx) => [id, idx]));
      return [...rowGroups].sort((a, b) => {
        const aId = a.vehicle.truckId;
        const bId = b.vehicle.truckId;
        const aIdx = aId != null ? orderIndex.get(aId) ?? -1 : -1;
        const bIdx = bId != null ? orderIndex.get(bId) ?? -1 : -1;
        if (aIdx === -1 && bIdx === -1) return 0;
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      });
    })();
    return applyHiddenFilter(ordered);
  }, [rowGroups, sortedTruckIds, applyHiddenFilter]);
  // getOrderedRowGroups（useRotationEditing へ渡す遅延参照）が読む最新値
  orderedRowGroupsRef.current = orderedRowGroups;

  // 車両リストの絞り込み。カード配車と違い、日付をまたいで持ち越す必要は無いので state のみ
  const [truckDisplayStatus, setTruckDisplayStatus] = useState<VehicleFilterMode>('すべて表示');
  // 回転はチェックボックスで多選択する。既定は全部（絞っていない状態）
  const [rotations, setRotations] = useState<RotationFilterValue[]>([...ALL_ROTATION_FILTERS]);
  // 制約違反のある車両だけ見る。既定はチェックなし
  const [constraintViolationOnly, setConstraintViolationOnly] = useState(false);
  // 表示対象（配送形態・区間種別）。既定は何も隠していない = すべて表示。
  // 表示項目と同じく localStorage に覚える（リロードで戻ると設定し直しになる）
  const [orderAttrFilter, setOrderAttrFilter] = useOrderAttrFilter();

  // 車両グループでの絞り込み。カード配車と同じ仕組みだが、選択は画面ごとに覚える
  // （/map とカード配車で見たいグループが違うため、localStorage のキーを分ける）
  // 1 台に複数のドライバーが乗る勤務があるので、行ごとの名前を引けるようにする
  // 勤務時間・車番も同じ理由で shift_truck ごとに引く
  const shiftTruckRowInfoById = useMemo(() => buildShiftTruckRowInfoMap(deliveries), [deliveries]);
  const { data: planningGroupsData } = useQueryPlanningGroups();
  // 選択は group id で覚えるので、ページが届いて一覧が増えても指す先はずれない
  // （useTruckGroupSelection）。読み込みを待たずに絞り込めるよう、途中でも一覧を作る
  const truckGroupEntitiesWithAll = useMemo(
    () => buildTruckGroupsWithAll(planningGroupsData ?? [], orderedRowGroups.map((g) => g.vehicle.truckId)),
    [planningGroupsData, orderedRowGroups],
  );
  // 画面を開くたびに「すべて表示する + 全グループ」から始める（V3Plans の
  // TruckSelectMenuPresenter と同じ）。持ち越さない理由は hook 側のコメント参照
  const {
    selectedIndices: selectedTruckGroupIndices,
    setSelectedIndices: setSelectedTruckGroupIndices,
    allowedTruckIds,
    // 履歴に載せるグループ。絞り込んでいなければ空で、buildMapPlanningSetting が
    // ID 0 の「すべて表示する」を送る
    selectedGroups: selectedTruckGroups,
  } = useTruckGroupSelection(truckGroupEntitiesWithAll);

  // 車両・車番・ドライバーの変更を、サーバ反映を待たずに見せる。/map の refetch は
  // 数秒かかるので、これが無いと「押しても変わらない」ように見える。
  // state はここで作る（下の useVehicleAssignmentEditing より前に displayRowGroups で使うため）
  const {
    overrides: vehicleOverrides, setVehicleOverride, clearVehicleOverride,
  } = useVehicleDisplayOverrides();
  // 積載以外の制約違反（勤務時間・指定時間・バース予約）。V3Plans と同じ判定・文言で出す。
  // 「制約違反のある車両のみ」の絞り込みも読むので displayRowGroups より前に置く
  const constraintAlertsByRowId = useMemo(
    () => buildConstraintAlertsByRowId(orderedRowGroups, deliveries),
    [orderedRowGroups, deliveries],
  );

  // 回転行ごとの積載量（同時に積んでいる量の最大）。勤務全体の operation を走査するので、
  // 絞り込み前の全グループから作る（絞ったあとの行で作ると、隠れている回転で積んだぶんが
  // 消えて表示中の行の積載量まで変わる）
  const peakLoadByRowId = useMemo(
    () => buildPeakLoadByRowId(orderedRowGroups),
    [orderedRowGroups],
  );

  /**
   * 表示日で絞った行。地図のルート・ピンもこれから作る（左リストと地図で見ている日が
   * 食い違わないようにする）。車両リストの絞り込み（配車済みのみ等）は地図に掛けない
   * 従来の切り分けを保つため、日だけを落としたこの段を分けて持つ。
   */
  const dayRowGroups = useMemo(
    () => filterRowGroupsByDay(orderedRowGroups, selectedDay, dayByShiftTruckId),
    [orderedRowGroups, selectedDay, dayByShiftTruckId],
  );

  /**
   * 表示日を変えたら、その日に無い行の選択を落とす。残すと画面に出ていない車両が
   * 自動配車や運賃計算の対象に入る。データ更新のたびに落とすと refetch の合間に
   * 選択が消えるので、日が変わったときだけ見る。
   */
  const prevSelectedDayRef = useRef(selectedDay);
  useEffect(() => {
    if (prevSelectedDayRef.current === selectedDay) return;
    prevSelectedDayRef.current = selectedDay;
    const dayRowIds = new Set(dayRowGroups.flatMap((g) => g.rows.map((r) => r.rowId)));
    setSelectedRowIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter((id) => dayRowIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectedDay, dayRowGroups]);

  const displayRowGroups = useMemo(
    () => filterVehicleRowGroups(
      applyVehicleOverrides(dayRowGroups, vehicleOverrides)
        .filter((g) => !allowedTruckIds || allowedTruckIds.has(g.vehicle.truckId)),
      {
        mode: truckDisplayStatus,
        selectedRowIds,
        rotations,
        constraintViolationOnly,
        constraintAlertsByRowId,
        peakLoadByRowId,
        orderAttrFilter,
      },
    ),
    [
      dayRowGroups, vehicleOverrides, allowedTruckIds, truckDisplayStatus, selectedRowIds,
      rotations, constraintViolationOnly, constraintAlertsByRowId, peakLoadByRowId, orderAttrFilter,
    ],
  );

  // 全選択の対象は「画面に見えている行」。絞り込み後の displayRowGroups から取る。
  // orderedRowGroups から取ると、グループや表示条件で隠れている車両まで自動配車の
  // 対象に入り、画面に出ていない車両が配車される
  const selectableRowIds = useMemo(() => collectSelectableRowIds(displayRowGroups), [displayRowGroups]);
  // 楽観表示が乗っている車両 = 直前に操作して refetch 待ちの車両。
  // 以前はこの車両のカードを緑にしていたが、色が変わると「何かおかしくなった」ように
  // 見えるため、実行中であることは右下のトースト（自動配車と同じ場所）で伝える
  const isApplyingVehicleChange = vehicleOverrides.size > 0;
  const allSelected = allRowsSelected(selectableRowIds, selectedRowIds);
  const onSelectAll = useCallback(() => setSelectedRowIds(new Set(selectableRowIds)), [selectableRowIds]);

  // orderIndexByTruckId: 左リスト表示順(orderedRowGroups)由来の唯一の Map。map(routes)と
  // 左リスト色バー(Task 7)の両方へこの同じ Map を渡すことで、車両 D&D 並替後も
  // palette fallback 色が一致する (Codex Finding 2)。
  const orderIndexByTruckId = useMemo(() => {
    const m = new Map<number, number>();
    orderedRowGroups.forEach((g, i) => {
      if (g.vehicle.truckId != null) m.set(g.vehicle.truckId, i);
    });
    return m;
  }, [orderedRowGroups]);

  // garageEntities は既に定義済み (発地行の車庫名解決に使う。routes/list 双方から参照するため
  // resolveGarage 自体は下の expandedRowVMByRowId に閉じ込め、prop としては渡さない)。
  const resolveGarage = useCallback(
    (deliveryId: number) => resolveGarageByDeliveryId(garageEntities, deliveryId),
    [garageEntities],
  );

  // 展開行(左リスト)と地図マーカーが同じ地点・同じ番号(seriesIndex)を出すための共有 VM。
  // 1 回だけ組んで routes / ConvenienceDispatchVehicleList の両方へ渡す (行ごと・画面ごとに
  // 計算すると番号が食い違うため)。orderedRowGroups/orderIndexByTruckId の生成後に定義する
  // (前方参照の TDZ を避けるため、このブロックの直後に配置している)。
  // 出発・帰着の地点と時刻は delivery の departure_operation / arrival_operation が正
  const terminalsByDeliveryId = useMemo(() => buildDeliveryTerminals(deliveries), [deliveries]);
  const resolveTerminals = useCallback(
    (deliveryId: number) => terminalsByDeliveryId.get(deliveryId),
    [terminalsByDeliveryId],
  );

  // ヘッダーの「計画」は勤務 (delivery) 全体の窓。旧マップ (/plans) と同じ値を出す
  // (log_web_issues#310)。1 delivery が cycle ごとに複数 row になるので delivery 単位で作る
  const planWindowByDeliveryId = useMemo(() => buildDeliveryPlanWindows(deliveries), [deliveries]);
  const resolvePlanWindow = useCallback(
    (deliveryId: number) => planWindowByDeliveryId.get(deliveryId),
    [planWindowByDeliveryId],
  );

  const expandedRowVMByRowId = useMemo(
    () => buildExpandedRowVMByRowId({
      rowGroups: orderedRowGroups,
      // 停車行の表示は数値指標しか読まない。実効集合を渡すと未割当リスト向けの項目を
      // 切り替えるだけで全行の VM を組み直すことになる
      displayMetrics: vehicleDisplayMetricSet,
      peakLoadByRowId,
      resolveGarage,
      resolveTerminals,
      resolvePlanWindow,
    }),
    [orderedRowGroups, vehicleDisplayMetricSet, peakLoadByRowId, resolveGarage, resolveTerminals, resolvePlanWindow],
  );

  // 左リスト色バーの表示色は、地図(routes)と同じ sourceRows.truckColor(=deliveries 由来)から引く。
  // trucks.color(v3 query)を使うと、mutation 成功時に deliveries と trucks を非同期 invalidate する
  // 都合で片方だけ先に refetch され map/list の色が一時的に割れる(Codex Finding 2)。両者を単一
  // ソース(deliveries)に集約して割れを防ぐ。trucks(truckById)はピッカーの現在色/運送会社情報用に残す。
  const truckColorByTruckId = useMemo(() => buildTruckColorMap(sourceRows), [sourceRows]);

  // 地図ルートは「可視行」(applyHiddenFilter 適用後の orderedRowGroups) から作る。
  // sourceRows(hook 出力・回転追加マージ後だが非表示フィルタ前)をそのまま使うと、
  // ユーザーが×で隠した placeholder rotation の線が地図にだけ残ってしまうため。
  // RotationRow.source は元の VehicleRowVM を保持しているのでそのまま渡せる。
  // 車庫までの線を引くのに使う（出発・帰着の判定は delivery が持つ）
  const deliveryById = useMemo(
    () => new Map(deliveries.map((d) => [d.id, d])),
    [deliveries],
  );

  const routes = useMemo(
    () => buildMapVehicleRoutes(
      dayRowGroups.flatMap((g) => g.rows.map((r) => r.source)),
      orderIndexByTruckId,
      expandedRowVMByRowId,
      truckColorByTruckId,
      deliveryById,
    ),
    [dayRowGroups, orderIndexByTruckId, expandedRowVMByRowId, truckColorByTruckId, deliveryById],
  );

  // 共通配送先ポップアップ(P3-b)の色。地図の走行ルート線と同じ値を渡すため routes から引く
  // (ここで resolveVehicleColor を呼び直すと、線と一覧で色が割れる余地を作ってしまう)。
  const colorByRowId = useMemo(
    () => new Map(routes.map((route) => [route.rowId, route.color])),
    [routes],
  );

  // 車両行の展開(P3-a)。リセットと prune は hook に閉じている
  const [expandedRowIds, onToggleRowExpanded, toggleAllExpanded] = useExpandedRowIds({
    startOn,
    endOn,
    companyId,
    rowGroups: orderedRowGroups,
  });

  // 「すべて展開」の対象は全選択と同じく「画面に見えている行」。orderedRowGroups から取ると
  // 絞り込みで隠れている行まで開き、絞り込みを外したときに身に覚えのない展開が並ぶ
  const expandableRowIds = useMemo(() => collectExpandableRowIds(displayRowGroups), [displayRowGroups]);
  const isAllRowsExpanded = allRowsExpanded(expandableRowIds, expandedRowIds);
  const onToggleAllExpanded = useCallback(
    () => toggleAllExpanded(expandableRowIds),
    [toggleAllExpanded, expandableRowIds],
  );

  // 左リスト色バー＆ピッカー(P1c)。ConvenienceDispatchVehicleList は memo なので、
  // vehicleColorFor/onVehicleColorClick は useCallback で参照等価性を保ち、
  // trucks/orderIndexByTruckId が変わらない限り memo の再レンダーを起こさないようにする。
  const [colorAnchor, setColorAnchor] = useState<{ el: HTMLElement; truckId: number } | null>(null);

  const truckById = useMemo(() => new Map(trucks.map((t) => [t.id, t])), [trucks]);

  // ピッカーの「対象」見出し用の車両名(左リスト表示と同じ truckName || vehicleNumber)を truckId で引く。
  const truckNameByTruckId = useMemo(() => {
    const m = new Map<number, string>();
    orderedRowGroups.forEach((g) => {
      const { truckId, truckName, vehicleNumber } = g.vehicle;
      if (truckId != null && !m.has(truckId)) m.set(truckId, truckName || vehicleNumber);
    });
    return m;
  }, [orderedRowGroups]);

  const onVehicleColorClick = useCallback((truckId: number, el: HTMLElement) => {
    setColorAnchor({ el, truckId });
  }, []);

  const vehicleColorFor = useCallback(
    (truckId: number) =>
      resolveVehicleColor(truckColorByTruckId.get(truckId) ?? null, orderIndexByTruckId.get(truckId) ?? 0),
    [truckColorByTruckId, orderIndexByTruckId],
  );

  // 毎レンダー作り直すと memo 済みの車両リストが未割当の選択でも再描画される
  const onRowToggle = useCallback((rowId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const handleDeselectAll = useCallback(() => setSelectedRowIds(new Set()), []);

  // ─────────────────────────────────────────────────────────────
  // 配車指示メール / 配送状況（旧地図配車と同じ。ドライバーアプリのライセンスがある会社だけ）
  // ─────────────────────────────────────────────────────────────
  const { operationSendMail } = useMutationPlanningOperations();
  const sendMailMutate = operationSendMail.mutate;
  // deliveries が揃うまでは空で呼ぶ。1 ページ届くたびに id 群が変わると、そのたびに
  // 問い合わせ直すことになる（V3Plans/Component.tsx:177 と同じ待ち方）
  const { data: lastOperationStatusUpdated } = useQueryLastOperationStatusUpdated(
    startOn,
    endOn,
    deliveriesHasNextPage ? [] : deliveries.map((d) => d.id),
  );

  const handleSendOperationMail = useCallback(() => {
    const shiftTruckIds = collectSelectedShiftTruckIds(selectedRowIds, rowGroups);
    if (shiftTruckIds.length === 0) return;

    setConfirmDialog({
      title: '配車指示メールを送りますか？',
      description: `${shiftTruckIds.length} 台のドライバーへ指示メールを送ります。`,
      onConfirm: () => {
        sendMailMutate({ startOn, endOn, shiftTruckIds }, {
          onSuccess: () => { notify('配車指示メールを送信しました', 'success'); },
          onError: (e: AxiosError<{ message: string }>) => {
            notify(e.response?.data?.message ?? '配車指示メールの送信に失敗しました', 'error');
          },
        });
      },
    });
  }, [selectedRowIds, rowGroups, sendMailMutate, startOn, endOn, notify]);

  // 配送状況は別 query なので、deliveries の取り直しと合わせて invalidate する
  const handleRefreshOperationStatuses = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: ['lastOperationStatusUpdated'] });
  }, [queryClient]);

  // 旧地図配車と同じく、平時は最終更新日時だけを出し、新しい更新を見つけたときだけ
  // 「N分前に更新あり」と再取得ボタンを出す
  const operationStatus = useOperationStatusFreshness({
    lastUpdatedAt: lastOperationStatusUpdated,
    onRefresh: handleRefreshOperationStatuses,
  });

  const operationStatusText = useMemo(() => {
    if (!useDriverApp) return undefined;
    if (operationStatus.agoMinutes > 0) return `${operationStatus.agoMinutes}分前に更新あり`;
    if (!operationStatus.lastUpdatedAt) return undefined;
    // formatInTimeZone を使う。date-fns-tz の format は timeZone を無視する（FE 既存バグ #2376）
    return `配送状況 ${formatInTimeZone(new Date(operationStatus.lastUpdatedAt), 'Asia/Tokyo', 'MM/dd HH:mm')}`;
  }, [useDriverApp, operationStatus.agoMinutes, operationStatus.lastUpdatedAt]);

  // ─────────────────────────────────────────────────────────────
  // 地図の表示フィルタ / 運行状況表示（旧地図配車 V3Plans presenters/ActionsOnMapPresenter.tsx）
  // ─────────────────────────────────────────────────────────────
  // 既定は全部 ON（旧地図配車 V3Plans/Presenter.tsx:687 と同じ）
  const [displayKinds, setDisplayKinds] = usePersistedJSON<string[]>(
    'mapDisplayKinds',
    [...DISPLAY_KIND_OPTIONS],
    isDisplayKindArray,
  );
  const [showTruckStatus, setShowTruckStatus] = useState(false);

  // 左右リストの幅。納品先名称が長いと読み切れないので可変にし、変えた幅は次回も再現する
  // (依頼 log_web_issues#299-1)。旧地図配車 (V3Plans/Presenter.tsx:387,407) は固定値
  const [leftPanelWidth, setLeftPanelWidth] = usePersistedJSON<number>(
    LEFT_PANEL_WIDTH_STORAGE_KEY,
    DEFAULT_LEFT_PANEL_WIDTH,
    isPanelWidth,
  );
  const [rightPanelWidth, setRightPanelWidth] = usePersistedJSON<number>(
    RIGHT_PANEL_WIDTH_STORAGE_KEY,
    DEFAULT_RIGHT_PANEL_WIDTH,
    isPanelWidth,
  );
  // 右ペインの幅には開閉アニメーションの transition が乗っている。ドラッグ中も効いたままだと
  // 指の動きに 0.2s 遅れて追いかけてくるので、掴んでいる間だけ切る
  const [rightPanelResizing, setRightPanelResizing] = useState(false);

  // 3 ペインを並べている器の幅。保存幅がこれを超えるときは描画時に落とす（fitPanelWidths）
  const paneContainerRef = useRef<HTMLDivElement>(null);
  const [paneContainerWidth, setPaneContainerWidth] = useState(0);
  // Why useLayoutEffect: 初回描画で幅が 0 のままだと、器に収まらない保存幅がそのまま
  // 1 フレーム描かれてから縮む（はみ出しが一瞬見える）
  useLayoutEffect(() => {
    const element = paneContainerRef.current;
    if (!element) return undefined;
    const measure = () => setPaneContainerWidth(element.getBoundingClientRect().width);
    measure();
    // ResizeObserver ならサイドナビの開閉のように window の resize を伴わない変化も拾える。
    // jsdom には無いので、その環境では window の resize で代替する
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // 右ペインの開閉。閉じても中身はマウントしたままにするので、選択・絞り込み・
  // 表示単位は保たれる
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  // 保存幅が器に収まらないときは描画時に落とす。ドラッグ開始時の上限だけだと
  // 「右を広げる → 畳む → 左を広げる → 右を開き直す」で地図が幅 0 になる
  const { left: fittedLeftPanelWidth, right: fittedRightPanelWidth } = useMemo(
    () => fitPanelWidths({
      containerWidth: paneContainerWidth,
      left: leftPanelWidth,
      right: rightPanelWidth,
      rightOpen: rightPanelOpen,
    }),
    [paneContainerWidth, leftPanelWidth, rightPanelWidth, rightPanelOpen],
  );

  const toggleRightPanel = useCallback(() => {
    ReactGA.event('click', {
      screen_name: SCREEN_NAMES.PLANNING_MAP,
      button_name: '右パネル開閉',
    });
    setRightPanelOpen((prev) => !prev);
  }, []);

  // 地図から車両を選んだら左リストをその行まで動かす（項目42）。
  // 左リストのクリック (onRowToggle) では動かさない。その場で見えているのに跳ねる
  const { registerRow, scrollToRow } = useScrollToRow();

  const onMarkerRowClick = useCallback((rowId: string) => {
    setSelectedRowIds((prev) => new Set(prev).add(rowId));
    scrollToRow(rowId);
  }, [scrollToRow]);

  // 右クリックは解除だけ。トグルにすると右クリックで選択が増えてしまう（旧地図と同じ）
  const onMarkerRowDeselect = useCallback((rowId: string) => {
    setSelectedRowIds((prev) => {
      if (!prev.has(rowId)) return prev;
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
  }, []);

  const onUnassignedDeselect = useCallback((id: string) => {
    setSelectedUnassignedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // ─────────────────────────────────────────────────────────────
  // 詳細パネル (P3-b)。実体は useDispatchDetailActions (カード配車と共有) で、
  // ここでは /map 固有の機構だけを注入する。
  // 「他車両へ配車」(Task 8) は openTransferPlanning / resetTransferPlanning を注入して解禁する
  // (下の自動配車ダイアログ・moveTarget state と合流する)。
  // ─────────────────────────────────────────────────────────────
  const applyResetToCaches = useApplyResetToCaches();
  const { bulkDelete: deleteShipmentOrders, cloneShipmentOrder } = useMutationShipmentOrder();
  const deleteShipmentOrdersAsync = deleteShipmentOrders.mutateAsync;

  /**
   * 選択した未割当案件をまとめて削除する。カード配車 (ConvenienceDispatches/Presenter.tsx:2315-2355)
   * と同じ流れ: 対象を shipment_order 単位に解決 → 確認 → 楽観的に消す → 失敗したら戻す。
   */
  const handleDeleteSelectedUnassigned = useCallback(() => {
    const ids = [...selectedUnassignedIds];
    if (ids.length === 0) return;

    const shipmentOrderIds = resolveShipmentOrderIdsFromUnassignedIds(ids, legRuns);
    if (shipmentOrderIds.length === 0) {
      notify('削除対象の案件が見つかりません', 'error');
      return;
    }

    // 1 shipment_order が複数 legRun を持つので、配下の legRun を全て画面から消す
    const shipmentOrderIdSet = new Set(shipmentOrderIds);
    const removedItemIds = new Set<string>();
    legRuns.forEach((lr) => {
      if (lr.id != null && shipmentOrderIdSet.has(lr.shipment_order_id)) removedItemIds.add(`lr-${lr.id}`);
    });

    setConfirmDialog({
      title: '案件を削除しますか？',
      description: `${shipmentOrderIds.length} 件の案件を削除します。元には戻せません。`,
      onConfirm: async () => {
        // API を待つと反映が遅いので先に画面から消す。失敗したら戻す
        const removal = unassignedOptimistic.remove(removedItemIds);
        try {
          await deleteShipmentOrdersAsync(shipmentOrderIds);
        } catch (e) {
          const err = e as AxiosError<{ message: string }>;
          notify(err.response?.data?.message ?? '削除に失敗しました', 'error');
          removal.rollback();
          return;
        }
        removal.commit();
        setSelectedUnassignedIds(new Set());
        notify(`${shipmentOrderIds.length} 件の案件を削除しました`, 'success');
      },
    });
  }, [selectedUnassignedIds, legRuns, unassignedOptimistic, deleteShipmentOrdersAsync, notify]);


  // ─────────────────────────────────────────────────────────────
  // 車両 / 車番 / ドライバーの差し替え (P5-a 項目9)。カード配車と同じ hook を使う。
  // 表示上書きは orderedRowGroups の手前ではなく後段（下記 displayRowGroups）で当てる
  // ─────────────────────────────────────────────────────────────
  const refetchAfterEdit = useCallback(async () => {
    // /map はデータを index.tsx の query が持つので、キーを invalidate して取り直す
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] }),
    ]);
  }, [queryClient]);

  const {
    handlers: vehicleAssignmentHandlers,
    menuProps: vehicleAssignmentMenuProps,
  } = useVehicleAssignmentEditing({
    rowGroups: orderedRowGroups,
    deliveries,
    trucks,
    drivers,
    refetchAfterEdit,
    notify,
    setVehicleOverride,
    clearVehicleOverride,
  });

  // 「計画無」バッジの × 。勤務ごと消す片道操作なので確認と実行中ガードを通す
  const { requestDeleteShift, deletingShiftTruckIds } = useShiftDeletion({
    deliveries,
    refetchAfterDelete: refetchAfterEdit,
    notify,
    confirm: setConfirmDialog,
    getBlockedReason: getBusyGuardReason,
  });

  // 削除中の勤務を画面から先に消す。失敗すれば集合から外れて戻る
  const visibleRowGroups = useMemo(
    () => hideDeletingShifts(displayRowGroups, deletingShiftTruckIds),
    [displayRowGroups, deletingShiftTruckIds],
  );

  /**
   * 消えた行の選択を外す。勤務や回転を削除しても「n件選択中」が減らないと、画面に
   * 無い車両が件数にも自動配車の対象にも残る（指摘 2026/08/19）。
   *
   * 判定は絞り込み前の集合 (orderedRowGroups) から作る。表示条件で隠れているだけの行まで
   * 外すと、選び直す手段ごと消える。取得中は見ない（日を切り替えた直後などに一瞬空になる）。
   */
  const existingRowIds = useMemo(
    () => new Set(
      hideDeletingShifts(orderedRowGroups, deletingShiftTruckIds)
        .flatMap((g) => g.rows.map((r) => r.rowId)),
    ),
    [orderedRowGroups, deletingShiftTruckIds],
  );
  useEffect(() => {
    if (isLoading) return;
    setSelectedRowIds((prev) => pruneSelectedRowIds(prev, existingRowIds));
  }, [existingRowIds, isLoading]);

  // ─────────────────────────────────────────────────────────────
  // 車両追加 / 帳票出力 (P5-a)。どちらもカード配車と同一の実体を使う
  // （帳票は /plans とも同じ useReportPrinting なので「旧地図配車のまま」を満たす）
  // ─────────────────────────────────────────────────────────────
  const [addVehicleDialogOpen, setAddVehicleDialogOpen] = useState(false);
  const openAddVehicleDialog = useCallback(() => setAddVehicleDialogOpen(true), []);
  // 既に配車済みの車両・ドライバーは候補から外す（二重追加を防ぐ）
  const existingTruckIds = useMemo(
    () => new Set(sourceRows.filter((r) => !r.isPlaceholderRow).map((r) => r.truckId)),
    [sourceRows],
  );
  const existingDriverIds = useMemo(() => new Set(deliveries.map((d) => d.driverId)), [deliveries]);

  const printTargetShiftIds = useMemo(
    () => resolvePrintTargetShiftIds(collectSelectedShiftTruckIds(selectedRowIds, orderedRowGroups), deliveries),
    [selectedRowIds, orderedRowGroups, deliveries],
  );
  const reportPrinting = useReportPrinting({
    startOn,
    endOn,
    shiftIds: printTargetShiftIds,
    onAnalyticsEvent: (action, buttonName) =>
      ReactGA.event(action, { screen_name: SCREEN_NAMES.PLANNING_MAP, button_name: buttonName }),
  });

  /**
   * 配車リセット（選択中の車両の案件を未割当へ戻す）。カード配車の
   * handleUnassignSelected と同じ順序: 確認 → API → キャッシュ反映 → 通知。
   *
   * Why not 選択解除: 戻したあと続けて同じ車両へ配車し直す使い方をするため、選択は残す
   * （既存のマップ画面も未割当に戻したあと選択が残る）。行ごと消えたときは
   * pruneSelectedRowIds が落とす。
   *
   * 対象は非表示フィルタ適用後の orderedRowGroups。全選択と同じ集合を見ないと、
   * 「選んだつもりの無い行」まで消える。
   */
  const handleUnassignSelected = useCallback(() => {
    const { legRunIds, deletableRowCount } = collectResetTargets(selectedRowIds, orderedRowGroups);
    if (deletableRowCount === 0) return;

    setConfirmDialog({
      title: '配送計画を削除しますか？',
      description: `${deletableRowCount}件の配送計画を削除します。`,
      onConfirm: async () => {
        // 自動配車の実行中に割当を消すと、計算結果と画面が食い違う（回転削除と同じガード）
        const busyReason = getBusyGuardReason();
        if (busyReason) { notify(busyReason, 'error'); return; }
        try {
          await resettingMutateAsync(legRunIds);
        } catch (e) {
          const err = e as AxiosError<{ message: string }>;
          notify(err.response?.data?.message ?? '処理に失敗しました', 'error');
          return;
        }
        applyResetToCaches(legRunIds);
        notify(`${deletableRowCount}件の配送計画を削除しました`, 'success');
      },
    });
  }, [selectedRowIds, orderedRowGroups, getBusyGuardReason, resettingMutateAsync, applyResetToCaches, notify]);

  /**
   * 選択中の 2 車両の配送計画を入れ替える（V3Plans の swapRequest 相当）。
   * 断る条件と文言は resolveSwapBlockReason に閉じ込めて /plans と揃える。
   *
   * 実行は controller.runSwap に任せる。入れ替えも queued で返って非同期に計算されるため、
   * 直接 mutate するとポーリングに乗らず、結果が反映されないまま完了扱いになる。
   * controller の定義より前に置けないので、実体は下の handleSwapSelected で組む。
   */
  const swapBlockReasonOf = useCallback(
    (deliveryIds: [number, number]) => resolveSwapBlockReason(deliveryIds, enrichedDeliveries),
    [enrichedDeliveries],
  );

  // ─────────────────────────────────────────────────────────────
  // 自動配車 (P4b2)。8 hooks は buildMapAutoDispatchHooks に集約する (/map は override を
  // 持たないためカード配車と中身が別物になる。設計書「各画面の実装担当」の /map 行)。
  // ─────────────────────────────────────────────────────────────
  // 未割当案件があるときの「理由」参照用 session（カード配車の notAllocSessionId と同じ役割）。
  const [notAllocSessionId, setNotAllocSessionId] = useState<string | null>(null);

  // 自動配車で新しく割り付いた案件のハイライト（設計書: 2026-08-10-map-newly-assigned-highlight-design.md）
  const {
    newlyAssignedByShiftTruck,
    takeSnapshot: takeAssignedSnapshot,
    setEnabled: setHighlightEnabled,
    clear: clearHighlight,
  } = useNewlyAssignedHighlight(enrichedDeliveries);

  const autoDispatchHooks = buildMapAutoDispatchHooks({
    notify,
    setNotAllocSessionId,
    takeAssignedSnapshot,
    setHighlightEnabled,
    clearHighlight,
    hideUnassigned: hideUnassignedForPlanning,
    revealUnassigned: revealUnassignedForPlanning,
    revealAllUnassignedNow,
    revealAllUnassignedAfterRefetch,
  });

  // 未割当理由バッジ (Task 9)。カード配車 (ConvenienceDispatches/Presenter.tsx) の 4 ステップを写す。
  // API の order_id は常に leg_run.id なので、そのまま key にする
  // (buildNotAllocReasonsByOrderId の Why not コメント参照。両建てにすると無関係な案件へ理由が付く)。
  const { data: notAllocReasonsData } = useQueryPlanningsNotAllocReasons(notAllocSessionId ?? '');

  // 未割当が出たときの「配車のヒント」。カード配車 (ConvenienceDispatches/Presenter.tsx:1044-1067)
  // と同じ session から引く。/map では配線が漏れていて出ていなかった
  const { data: notAllocSuggestions } = useQueryPlanningsSuggestions(notAllocSessionId ?? '');
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
  const [showNotAllocSuggestionSnackbar, setShowNotAllocSuggestionSnackbar] = useState(false);
  useEffect(() => {
    setShowNotAllocSuggestionSnackbar((notAllocSuggestions?.length ?? 0) > 0);
  }, [notAllocSuggestions]);
  const handleSuggestionDialogClose = useCallback(() => setSuggestionDialogOpen(false), []);
  const notAllocReasonsByOrderId = useMemo(
    () => buildNotAllocReasonsByOrderId(notAllocReasonsData),
    [notAllocReasonsData],
  );

  // controller の戻り値 (isBusy/runNormal 等) はツールバーの「自動配車」ボタン (Task 6-b) が
  // 使う。8 hooks を配線し、scope 変更時の破棄・polling 等の内部 effect を有効にする。
  const controller = useAutoDispatchController({
    scope: { startOn, endOn, companyId },
    hooks: autoDispatchHooks,
    deps: {
      // normal/transfer/timeline/operationBulkResetting は 1 箇所の useMutationPlanningV5()
      // から共有する。controller が独自インスタンスを持つと、operationBulkResetting.isPending
      // (運賃計算の isResetting 判定・useDispatchDetailActions の手動リセットに使う) は
      // 本番では別インスタンスの pending を反映しなくなる (useMutation は呼び出しごとに
      // 独立した observer を作るため。カード配車と同じ理由・controllerSharedMutationInstance.test.tsx)
      usePlanningMutations: () => ({
        normalPlanning, transferPlanning, timelinePlanning, swapPlanning, operationBulkResetting,
      }),
    },
  });

  // Task 10: busyGuardRef の値の同期。render 本体で代入しない (React 19 は render を
  // 中断・破棄しうるので、破棄された render の値が ref に残り、コミット済み UI の
  // イベントが「画面に出ていない状態」を読む)。commit 後に同期すれば
  // 「コミット済み UI ↔ ref」が常に一致する。deps を付けない (毎コミット同期する)。
  useLayoutEffect(() => {
    busyGuardRef.current = resolveBusyGuardReason({
      isBusy: controller.isBusy,
      isResetting: operationBulkResetting.isPending,
    });
  });

  // ─────────────────────────────────────────────────────────────
  // ツールバーの「自動配車」ボタン (Task 6-b)。設定 state・PlanningEntity 組み立ては
  // usePlanningDialogSettings / buildPlanningEntity (Task 6-a) に共通化済み。
  // ─────────────────────────────────────────────────────────────
  const [planningDialogOpen, setPlanningDialogOpen] = useState(false);
  // 移動モード: 詳細パネルの「他車両へ配車」経由でダイアログを開いたとき target 情報を保持する。
  // セット済みのときは handlePlanningRequest が controller.runTransfer を使う
  // (ConvenienceDispatches/Presenter.tsx:2301-2303 と同じ役割)。
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const relaxedRules = useMemo(() => RelaxedRuleValues.map((it) => it), []);
  const [selectedRelaxedRules, setSelectedRelaxedRules] = useState<RelaxedRuleVo[]>([]);
  const [priorityLargeTrucks, setPriorityLargeTrucks] = useState<number>(0);
  const { settings: planningDialogSettings, presenterProps: planningDialogSettingProps } = usePlanningDialogSettings();

  // ID の組み立ては /map 側の責務 (設計書 :203-218)。選択行 → delivery.id (= shift_truck.id) →
  // delivery.shiftId (自動配車が要求する shift.id) の順で写す。同じ shift.id を複数の
  // shift_truck (複数回転) が指すことがあるため、写したあとに改めて重複排除する
  // (collectSelectedShiftTruckIds 自体の uniq は shift_truck.id 単位なので別物)。
  // 移動モード (moveTarget セット済み) では選択行ではなく moveTarget から導出した値を優先する
  // (ConvenienceDispatches/Presenter.tsx:2339-2355 と同じ規則)。
  const selectedShiftIds = useMemo(() => {
    if (moveTarget) return [moveTarget.targetShiftId];
    // 回転追加した行だけを選んだときも同じ車両の勤務へ解決する（V3Plans と同じ）。
    // 確定用の collectSelectedShiftTruckIds とは用途が違うので判定を共有しない
    const deliveryIds = collectSelectedDeliveryIdsForPlanning(selectedRowIds, rowGroups);
    const shiftIdSet = new Set<number>();
    deliveryIds.forEach((deliveryId) => {
      const shiftId = deliveries.find((d) => d.id === deliveryId)?.shiftId;
      if (shiftId != null) shiftIdSet.add(shiftId);
    });
    return [...shiftIdSet];
  }, [moveTarget, selectedRowIds, rowGroups, deliveries]);

  const mailAvailability = useMemo(
    () => resolveOperationMailAvailability({
      useDriverApp,
      selectedShiftTruckCount: selectedShiftIds.length,
    }),
    [useDriverApp, selectedShiftIds.length],
  );


  // 選択した未割当案件の 積地→降地。点だけでは「どこからどこへ運ぶか」が読めない
  const unassignedPolylines = useMemo(
    () => buildUnassignedPolylines(selectedUnassignedIds, legRuns),
    [selectedUnassignedIds, legRuns],
  );

  const selectedOrderIds = useMemo(() => {
    if (moveTarget) return moveTarget.orderIds;
    return parseUnassignedIds(selectedUnassignedIds);
  }, [moveTarget, selectedUnassignedIds]);

  // Task 10: UI 層 (disabled/Tooltip) の共通理由。mutate 直前ガード (getBusyGuardReason /
  // resolveBusyGuardReason) と判定条件を揃える。#1 配車確定・確定解除 と #2 回転削除の
  // ボタン両方がこれを使う (ConvenienceDispatchVehicleList 経由で VehicleInfoView へ中継)。
  const busyDisabledReason = useMemo(
    () => resolveBusyGuardReason({
      isBusy: controller.isBusy,
      isResetting: operationBulkResetting.isPending,
    }) ?? undefined,
    [controller.isBusy, operationBulkResetting.isPending],
  );

  // 運賃計算。実行中フラグを進行トーストと可否判定の両方で使うので、ここで先に作る
  const [tariffDialogOpen, setTariffDialogOpen] = useState(false);
  const tariffAction = useTariffCalculationAction();

  // サーバが受け付けてから出す。POST が返る前は planning がまだ作られておらず、
  // 「計算中」と言いながら止める対象が無い状態になる
  // (旧地図配車も queued を確認してから出す。V3Plans/Component.tsx:952)
  const progressToastLabel = resolveProgressToastLabel({
    isResetting: operationBulkResetting.isPending,
    isAutoDispatching: controller.isAccepted,
    isApplyingVehicleChange,
    isCalculatingTariff: tariffAction.isExecuting,
  });

  // 層 2 (disabled・ツールチップ)。車両未選択はここに入れない
  // (層 3 の担当。resolveAutoDispatchAvailability.ts の Why not コメント参照)。

  const runSwap = controller.runSwap;

  /**
   * 入れ替えの実行。断る条件を先に見てから controller に渡す。
   * controller に乗せることで、queued で返ったときも normal/transfer と同じく
   * ポーリング → 完了通知 → キャッシュ更新の流れに入る。
   */
  const handleSwapSelected = useCallback((fromDeliveryId: number, toDeliveryId: number) => {
    const deliveryIds: [number, number] = [fromDeliveryId, toDeliveryId];
    const blockReason = swapBlockReasonOf(deliveryIds);
    if (blockReason) { notify(blockReason, 'error'); return; }

    setConfirmDialog({
      title: '配送計画を入れ替えますか？',
      description: '選択した2車両の配送計画を入れ替えます。',
      onConfirm: () => {
        // 自動配車の実行中に割当を動かすと、計算結果と画面が食い違う（配車リセットと同じガード）
        const busyReason = getBusyGuardReason();
        if (busyReason) { notify(busyReason, 'error'); return; }
        setSelectedRowIds(new Set());
        runSwap({ deliveryIds });
      },
    });
    // controller の戻り値は毎レンダー新しいオブジェクト。runSwap は useCallback で
    // 安定しているので、そちらを依存にする
  }, [swapBlockReasonOf, getBusyGuardReason, runSwap, notify]);

  const autoDispatchAvailability = useMemo(
    () => resolveAutoDispatchAvailability({
      isLoading,
      useAlgorithmPlanning,
      selectedCompanyId,
      isBusy: controller.isBusy,
      isResetting: operationBulkResetting.isPending,
      kind: 'normal',
    }),
    [isLoading, useAlgorithmPlanning, selectedCompanyId, controller.isBusy, operationBulkResetting.isPending],
  );

  // 選択の状態で決まる可否。ライセンス・実行中とは層を分ける（混ぜるとどちらを直せば
  // 押せるのか利用者に伝わらない）。選択した車両がすべて計画無なら案件の選択が要る
  const autoDispatchSelectionReason = useMemo(() => {
    const hasSelectedVehicle = orderedRowGroups.some(
      (g) => g.rows.some((r) => selectedRowIds.has(r.rowId)),
    );
    return resolveAutoDispatchSelectionReason({
      hasSelectedVehicle,
      // 車両ではなく選んだ勤務で見る。1 台に複数のドライバーが乗る勤務では、
      // 別の人に配車があるだけで「組み直せる」と誤判定してしまう
      allSelectedVehiclesHaveNoPlan: allSelectedShiftsHaveNoPlan(selectedRowIds, orderedRowGroups),
      hasSelectedUnassigned: selectedUnassignedIds.size > 0,
    });
  }, [orderedRowGroups, selectedRowIds, selectedUnassignedIds]);

  const isAutoDispatchDisabled = autoDispatchAvailability.disabled || autoDispatchSelectionReason !== null;
  // ライセンス等の理由を優先する。選択を直しても押せないものを先に伝える
  const autoDispatchTooltip = autoDispatchAvailability.disabled
    ? autoDispatchAvailability.tooltip
    : autoDispatchSelectionReason ?? autoDispatchAvailability.tooltip;

  // シナリオ配車の層 2。disabled の判定条件は自動配車と同じ (useAlgorithmPlanning・
  // selectedCompanyId・isBusy・isResetting)。V3 の ActionsPresenter.tsx:244 も
  // シナリオボタンの disabled に useAlgorithmPlanning を使っており、useScenarioPlanning
  // では判定しない（層 1 の「描くか」だけが useScenarioPlanning を見る。設計書 :161）。
  // kind: 'scenario' を渡すことで、有効時のツールチップだけ「シナリオ配車する」に変わる。
  const { disabled: isScenarioDispatchDisabled, tooltip: scenarioDispatchTooltip } = useMemo(
    () => resolveAutoDispatchAvailability({
      isLoading,
      useAlgorithmPlanning,
      selectedCompanyId,
      isBusy: controller.isBusy,
      isResetting: operationBulkResetting.isPending,
      kind: 'scenario',
    }),
    [isLoading, useAlgorithmPlanning, selectedCompanyId, controller.isBusy, operationBulkResetting.isPending],
  );

  // 層 3 (押したあとの検査)。V3Plans の planningButtonOnClick と同じ形 (押せるが検査して弾く)。
  // カード配車 3 箇所と同じ句点なしの文言にする (V3Plans の句点ありは実装差・本計画のスコープ外)。
  const handleAutoDispatchClick = useCallback(() => {
    if (selectedShiftIds.length === 0) {
      enqueueSnackbar('配車する車両を選択してください');
      return;
    }
    setPlanningDialogOpen(true);
  }, [selectedShiftIds, enqueueSnackbar]);

  const handlePlanningDialogClose = useCallback(() => {
    setPlanningDialogOpen(false);
    // scope 変更時 (resetTransferPlanning 経由) にも再利用するリセット。ここで捨てないと、
    // 別の日付/会社の shiftId へ移そうとしたり、次に開く通常の自動配車ダイアログが
    // 移動モードのまま (selectedShiftIds が moveTarget 由来) になってしまう。
    setMoveTarget(null);
  }, []);

  // シナリオ配車 (Task 7)。層 3 は「押したらダイアログを開くだけ」
  // (V3 の onClickScenarioPlanning・ActionsPresenter.tsx:231-234 と同じ。車両選択の検査は無い
  // — シナリオはコンテキスト全体を対象にする実行方式で、自動配車と違い選択行に依存しないため)。
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const handleScenarioDispatchClick = useCallback(() => setScenarioDialogOpen(true), []);

  // ScenarioPlanningListDialog が「実行」で呼ぶ。入力は scenarioId だけ
  // (startOn/endOn は controller が scope から補う。useAutoDispatchController.ts の
  // runScenario・RunScenarioInput 参照。ここで startOn/endOn を足すと二重になる)
  const requestScenarioPlanning = useCallback((scenarioId: number) => {
    controller.runScenario({ scenarioId });
  }, [controller]);

  // シナリオ履歴 (planning_setting) に残す画面条件。実行のたびに読むだけなので、
  // 通知してきた最後の値を持つ state に留める。
  //
  // **setter をそのまま子へ渡すこと。** 通知元 (ConvenienceDispatchUnassignedList /
  // PlanningPresenter) はどちらも通知用 useEffect の依存配列にコールバック自身を含むため、
  // インラインのアロー関数を渡すと親が再描画するたびに同じ内容の通知が再送される。
  // useState の setter は参照が固定されているのでこれを満たす。
  const [scenarioFilter, setScenarioFilter] = useState<MapScenarioFilterV1>(
    // 通知前 (未割当リストのマウント直後) だけ使う空条件。形を二重定義しないよう
    // 保存側と同じ純粋関数から作る
    () => buildMapScenarioFilter('', [], new Map()),
  );
  // PlanningPresenter は自動配車ダイアログを開くまでマウントされないので、それまでは null。
  // 既定値を捏造すると PlanningPresenter の初期値と二重定義になり、画面と履歴が食い違う
  const [planningConditions, setPlanningConditions] = useState<PlanningConditions | null>(null);

  // PlanningPresenter が「実行する」を押したときに呼ぶ。通常モードは controller.runNormal、
  // 移動モード (moveTarget セット済み・Task 8) は controller.runTransfer を使う
  // (ConvenienceDispatches/Presenter.tsx:2357-2405 と同じ分岐)。
  const handlePlanningRequest = useCallback((shiftIds: number[], orderIds: number[]) => {
    if (shiftIds.length === 0) {
      enqueueSnackbar('配車する車両を選択してください');
      return;
    }
    const planning = buildPlanningEntity(planningDialogSettings, {
      startOn, endOn, selectedRelaxedRules, priorityLargeTrucks,
    });

    if (moveTarget) {
      // 前段 reset (未割当への戻し) は controller.runTransfer 内部の担当。
      // /map から operationBulkResetting を直接呼ばない (設計書の分担どおり)
      void controller.runTransfer({
        orderIds: moveTarget.orderIds,
        transferToShiftId: moveTarget.targetShiftId,
        transferFromShiftId: moveTarget.sourceShiftId,
        planning,
      });
      setMoveTarget(null);
      return;
    }

    // 「どの勤務のどの回転を対象にするか」と「今の運行順」を送る。空で送っていたため
    // 2 回転目だけを選んでもサーバへ伝わらなかった（V3Plans hooks/usePlanningRequest.ts:88-166）
    const selectedCycleIndexes = buildSelectedCycleIndexes(selectedRowIds, rowGroups, enrichedDeliveries);
    const deliveryOperations = buildDeliveryOperations(
      selectedCycleIndexes.map((it) => it.deliveryId),
      enrichedDeliveries,
    );

    controller.runNormal({
      shiftIds,
      orderIds,
      planning,
      requestAsync: true,
      selectedCycleIndexes,
      deliveryOperations,
      // 履歴は「実行に使った選択状態」と同じ selectedCycleIndexes から組む。
      // ここで作り直すと、実行した内容と履歴の内容がずれる
      planningSetting: buildMapPlanningSetting({
        vehicleFilterMode: truckDisplayStatus,
        allDeliverySelected: allSelected,
        // 履歴に持てない絞り込み。掛かっていれば allDeliverySelected を倒して個別選択で保存する
        rotations,
        constraintViolationOnly,
        selectedCycleIndexes,
        deliveries: enrichedDeliveries,
        trucks,
        drivers,
        selectedTruckGroups,
        scenarioFilter,
        planningConditions,
      }),
    });
  }, [
    startOn,
    endOn,
    selectedRelaxedRules,
    priorityLargeTrucks,
    planningDialogSettings,
    controller,
    moveTarget,
    enqueueSnackbar,
    selectedRowIds,
    rowGroups,
    enrichedDeliveries,
    truckDisplayStatus,
    allSelected,
    rotations,
    constraintViolationOnly,
    trucks,
    drivers,
    selectedTruckGroups,
    scenarioFilter,
    planningConditions,
  ]);

  // 並べ替えの保存。専用 API は無く、自動配車の順序固定実行で保存する
  // (V3Plans Component.tsx:867-915 requestSingleAlgorithmPlanning と同じ)。
  const saveReorder = useCallback((payload: ReorderSavePayload) => {
    const delivery = enrichedDeliveries.find((it) => it.id === payload.deliveryId);
    if (!delivery) return;

    controller.runNormal({
      shiftIds: [delivery.shiftId],
      orderIds: [],
      planning: buildReorderPlanningEntity({
        startOn,
        endOn,
        orderOperationIdsForSort: payload.sortedOperationIds,
        orderOperationCycleIndexes: payload.orderOperationCycleIndexes,
        loadAfterInitialStock,
        setCurrentTimeToAlgorithm,
      }),
      requestAsync: true,
      // 対象は planning の operation 列で決まる。ここで回転を絞ると二重指定になる
      // (V3Plans も空で送っている。Component.tsx:936-937)
      selectedCycleIndexes: [],
      deliveryOperations: [],
      planningSetting: null,
    });
  }, [enrichedDeliveries, controller, startOn, endOn, loadAfterInitialStock, setCurrentTimeToAlgorithm]);

  // 保存ボタンは出さず、操作が終わってから 2 秒で送る。
  // 1 手ごとに送ると自動配車が連続で走って計算が詰まる
  const { schedule: scheduleReorderSave } = useDebouncedSave(saveReorder);

  // 展開行の停車地を並べ替えたとき。対象の回転だけ新しい順にし、他の回転は今の順のまま送る
  const handleReorderStops = useCallback((row: RotationRow, stops: ExpandedStopVM[]) => {
    if (row.source.isPlaceholderRow) return;
    // 保存の完了を待たずに並びを見せる。掴んだ行だけを差し替える
    setStopOrderPreview((prev) => {
      const next = new Map(prev);
      next.set(row.rowId, stops.flatMap((stop) => stop.operationIds));
      return next;
    });
    const deliveryId = row.source.deliveryId;
    const cycles = buildCyclesFromDelivery(enrichedDeliveries.find((it) => it.id === deliveryId));
    scheduleReorderSave({
      deliveryId,
      sortedOperationIds: buildSortedOperationIds(
        cycles,
        row.rotationNumber,
        stops.map((stop) => ({ operationIds: stop.operationIds })),
      ),
    });
  }, [enrichedDeliveries, scheduleReorderSave]);

  // 回転を並べ替えたとき。回転番号は動かしたあとの並びで振り直して送る。
  // 回転は勤務 (delivery) の中の並びなので、対象は掴んだカードの勤務。truckId から引くと
  // 1 台に 2 つの勤務が乗るときどちらを動かしても先頭の勤務を並べ替えてしまう
  const handleReorderRotations = useCallback((
    shiftTruckId: number | null, fromRotationNumber: number, toRotationNumber: number,
  ) => {
    const delivery = enrichedDeliveries.find((it) => it.id === shiftTruckId);
    if (!delivery) return;

    scheduleReorderSave({
      deliveryId: delivery.id,
      ...buildCycleReorderPayload(
        buildCyclesFromDelivery(delivery), fromRotationNumber, toRotationNumber,
      ),
    });
  }, [enrichedDeliveries, scheduleReorderSave]);

  // Presenter は query オブジェクトを持たない (handleRestoreSplit と同じ規則) ので、
  // 分割成功後の未割当リスト更新は invalidateQueries で行う。refetch prop は作らない。
  const refetchLegRuns = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
  }, [queryClient]);

  // 未割当ピンのホバーに出す軽い内容。詳細カードはクリックのときだけ出す
  // (ホバーで詳細カードを出すと、地図を横切るだけで開閉して読めない)
  //
  // ピンは地点ごとに 1 本なので、引き当てキーはマーカーの代表 id にする
  // (log_web_issues#2430)。同じ地点の複数案件は合算したサマリ 1 つにまとまる。
  const unassignedTooltips = useMemo(
    () => buildUnassignedTooltipMap(unassignedMarkers, unassignedItems),
    [unassignedMarkers, unassignedItems],
  );

  // 詳細カードを開いている未割当ピン。同じピンの再クリックで閉じる判定にだけ使い、
  // 描画には使わないので state ではなく ref に置く
  // (ここで再描画すると地図とマーカー全体が作り直しになり重い)。
  const pinnedUnassignedIdRef = useRef<string | null>(null);
  const clearHoverPin = useCallback(() => {
    pinnedUnassignedIdRef.current = null;
  }, []);

  // 共通配送先ポップアップの対象地点。VM ではなくキーだけを持つ。
  // VM を state に持つと、削除・再取得でデータが変わっても古い一覧を出し続けてしまう。
  const [sharedDestPlaceKey, setSharedDestPlaceKey] = useState<string | null>(null);

  // hook は自分の state しか破棄できないので、画面ローカルはここで全件捨てる。
  // 展開行 (expandedRowIds) は useExpandedRowIds が scope 依存で自前リセット済み。
  const handleScopeChanged = useCallback(() => {
    setSelectedRowIds(new Set());
    setSelectedUnassignedIds(new Set());
    setColorAnchor(null);
    setSharedDestPlaceKey(null);
    clearHoverPin();
  }, [clearHoverPin]);

  // 「他車両へ配車」(Task 8): 移動先が選ばれたら moveTarget を確定し、自動配車ダイアログを
  // 移動モードで開く (ConvenienceDispatches/Presenter.tsx:2446-2449 と同じ役割)。
  const openTransferPlanning = useCallback((target: MoveTarget) => {
    setMoveTarget(target);
    setPlanningDialogOpen(true);
  }, []);

  const detailActions = useDispatchDetailActions({
    deliveries,
    // 可視行 (非表示 rotation を除いた orderedRowGroups) を渡す。地図・左リストと同じ母集団にする
    rowGroups: orderedRowGroups,
    legRuns,
    screenName: SCREEN_NAMES.PLANNING_MAP,
    scopeKey,
    applyResetToCaches,
    operationBulkResetting,
    deleteShipmentOrders,
    cloneShipmentOrder,
    refetchLegRuns,
    setConfirmDialog,
    notify,
    unassignedOptimistic,
    // ✕ や操作完了で閉じたらピン留めも解除する。残すと以後のホバーが一切効かなくなる
    onCloseDetail: clearHoverPin,
    onScopeChanged: handleScopeChanged,
    openTransferPlanning,
    // ダイアログの close (キャンセル・実行いずれも PlanningPresenter が onClose を先に呼ぶ) と
    // scope 変更 (hook 内部の effect) の両方から呼ばれる共有のリセット
    resetTransferPlanning: handlePlanningDialogClose,
    // Task 10 #4・#5: 未割当へ戻す・案件削除の mutation 直前ガード。
    getBusyGuardReason,
  });
  const {
    detailSelection, detailOpen, openDetail, closeDetail,
    moveMenuAnchor, setMoveMenuAnchor, duplicateStoreNames, geoAlerts,
  } = detailActions;

  const unassignedItemById = useMemo(
    () => new Map(unassignedItems.map((item) => [item.id, item])),
    [unassignedItems],
  );

  // 詳細と共通配送先は排他表示 (どちらも地図右上の同じ位置に出るため重ねられない)。
  // 開く経路が 5 つあるので、閉じる責務は入口ごとではなくこの 1 本に集約する。
  // 位置は地図右上固定なので clickRect は使わない (placement='mapTopRight')。
  const openDetailExclusive = useCallback((selection: DispatchSelection) => {
    setSharedDestPlaceKey(null);
    openDetail(selection, null);
  }, [openDetail]);

  // クリック系の入口は共通でトグルする (同じ対象の再クリックで閉じる)。
  const openDetailFromClick = useCallback((selection: DispatchSelection) => {
    if (!selection) return;
    // 別経路で開いたらピン留めは解除する (ピン留めが残るとホバーが効かなくなる)
    clearHoverPin();
    if (isSameDetailTarget(detailSelection, selection)) {
      closeDetail();
      return;
    }
    openDetailExclusive(selection);
  }, [clearHoverPin, closeDetail, detailSelection, openDetailExclusive]);

  // 入口 1: 車両展開行の停車地クリック (積地・降地とも)
  const handleSelectStop = useCallback(
    (card: DispatchCard, vehicle: Vehicle, dispatchOrderIndex: number) => {
      openDetailFromClick({ source: 'vehicle', card, vehicle, orderIndex: dispatchOrderIndex });
    },
    [openDetailFromClick],
  );

  // 入口 2: 地図の配送マーカークリック。行選択 (onMarkerRowClick) は markerEvents 側で別途走る。
  // 同じ地点に 2 台以上いるときは詳細ではなく共通配送先を出す (判定は buildSharedDestinationVM)。
  const handleMarkerClick = useCallback((rowId: string, marker: MapRouteMarkerVM) => {
    const selection = resolveSelectionFromStop(rowId, marker, orderedRowGroups);
    // 積地カード (LoadingCard) は同じ拠点を多数が使うため分納ではない。降地だけを見る
    const placeKey = selection?.source === 'vehicle' && selection.card.type === 'delivery'
      ? selection.card.placeKey
      : undefined;
    // 「開けるかどうか」はここで確かめる。キーだけ入れて描画側で null になると
    // 押しても何も起きないマーカーができる
    const canShowShared = placeKey != null
      && buildSharedDestinationVM(placeKey, orderedRowGroups, expandedRowVMByRowId, colorByRowId) != null;

    // 同じ納品先を複数台で配送しているときは、その地点に来る車両・回転をまとめて選ぶ。
    // 押した行だけだと「1 台しか選ばれない」（積地は同じ拠点を多数が使うので対象外）
    if (placeKey != null) {
      const rowIdsAtPlace = collectRowIdsAtPlace(placeKey, orderedRowGroups);
      if (rowIdsAtPlace.length > 1) {
        setSelectedRowIds((prev) => {
          const next = new Set(prev);
          rowIdsAtPlace.forEach((id) => next.add(id));
          return next;
        });
      }
    }

    if (canShowShared) {
      // 同じ地点の再クリックで閉じる (詳細パネルと同じトグル)
      setSharedDestPlaceKey((prev) => (prev === placeKey ? null : placeKey));
      // 排他表示。ピン留めも解除される (closeDetail → onCloseDetail)
      closeDetail();
      return;
    }
    openDetailFromClick(selection);
  }, [closeDetail, colorByRowId, expandedRowVMByRowId, openDetailFromClick, orderedRowGroups]);

  // 入口 6: 共通配送先の「この配車の詳細」。展開行クリック (入口 1) と同じ解決経路を通し、
  // どの入口から開いても同じカードが出るようにする
  const handleSelectSharedEntry = useCallback((entry: SharedDestinationEntryVM) => {
    const selection = resolveSelectionFromStop(entry.rowId, entry, orderedRowGroups);
    // 解決できないときは一覧を開いたままにする (押せるのに何も起きない状態を作らない)
    if (!selection) return;
    clearHoverPin();
    openDetailExclusive(selection);
  }, [clearHoverPin, openDetailExclusive, orderedRowGroups]);

  // 入口 3: 地図の未割当ピン クリック = ピン留め
  const handleUnassignedClick = useCallback((id: string, context?: GroupedPinContext) => {
    const item = unassignedItemById.get(id);
    // 解決できないときは状態も変えない (押せるのに何も起きない状態を作らない)
    if (!item) return;

    // 地図から案件を選べるようにする（項目40）。クラスターは配下の click を
    // まとめて呼ぶので、ここが積み上がることでクラスター選択も成立する（項目46）。
    //
    // トグルにしない。ピンのクリックは詳細パネルも開くので、選択済みのピンを見に
    // いくたびに選択が外れてしまう。解除は右クリック（項目41・旧地図と同じ分担）
    setSelectedUnassignedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));

    // 集合状態のピンは詳細を出さない。クラスターも、同一地点をまとめた 1 本のピンも
    // 配下の click を全部呼ぶので、まとまっている案件のうち最後の 1 件だけが出て
    // 紛らわしい（選択はする）
    if (context?.grouped) return;

    // 同じピンの再クリックで閉じる (closeDetail → onCloseDetail でピン留めも解除される)
    if (pinnedUnassignedIdRef.current === id) {
      closeDetail();
      return;
    }
    pinnedUnassignedIdRef.current = id;
    openDetailExclusive({ source: 'unassigned', item });
  }, [closeDetail, openDetailExclusive, unassignedItemById]);

  // 入口 5: 未割当リストの詳細ボタン。カード配車と同じくトグルせず常に開く
  // (rect は mapTopRight では使わない)
  const handleShowUnassignedDetail = useCallback((item: UnassignedItem) => {
    clearHoverPin();
    openDetailExclusive({ source: 'unassigned', item });
  }, [clearHoverPin, openDetailExclusive]);

  /**
   * 地図の何も無いところを押したとき。開いているパネルを閉じる。
   *
   * 「×」を押さない限り残るのが分かりにくかった。閉じるのはパネルだけで、行や案件の
   * 選択は解除しない（選択の解除は右クリックに割り当てている操作）。
   */
  const handleMapBackgroundClick = useCallback(() => {
    setSharedDestPlaceKey(null);
    closeDetail();
  }, [closeDetail]);

  // 対象地点から一覧を組み直す。データが変わって分納でなくなったら null になり、
  // 古い一覧が残らない (state に VM を持たない理由)。
  const sharedDestinationVM = useMemo(
    () => (sharedDestPlaceKey == null
      ? null
      : buildSharedDestinationVM(sharedDestPlaceKey, orderedRowGroups, expandedRowVMByRowId, colorByRowId)),
    [sharedDestPlaceKey, orderedRowGroups, expandedRowVMByRowId, colorByRowId],
  );

  const detailPanelProps = useMemo(() => {
    if (!detailSelection) return null;
    const deliveryCard =
      detailSelection.source === 'vehicle' && detailSelection.card.type === 'delivery'
        ? detailSelection.card
        : null;
    const selectedId =
      detailSelection.source === 'vehicle' ? detailSelection.card.id : detailSelection.item.id;
    return {
      hasDuplicate: deliveryCard ? duplicateStoreNames.has(deliveryCard.storeName) : false,
      geoAlert: geoAlerts.get(selectedId),
    };
  }, [detailSelection, duplicateStoreNames, geoAlerts]);

  // 「他車両へ配車」(Task 8) の移動先候補。rowId → shiftId の解決は rowIdToShiftTruckId
  // (deliveries 由来) を介す (ConvenienceDispatches/Presenter.tsx:2884-2904 と同じ手順)。
  const rowIdToShiftTruckId = useMemo(() => buildRowIdToShiftTruckId(deliveries), [deliveries]);

  const moveTargets = useMemo((): DetailPanelMoveTarget[] => {
    if (!detailSelection || detailSelection.source !== 'vehicle' || detailSelection.card.type !== 'delivery') {
      return [];
    }
    const sourceCard = detailSelection.card;
    const shiftIdOfRow = (rowId: string) => {
      const delivery = deliveries.find((d) => d.id === (rowIdToShiftTruckId.get(rowId) ?? -1));
      return delivery?.shiftId ?? null;
    };
    // 選択中カードが載っている row (= 移動元)。可視行 (orderedRowGroups) の中から探す
    const currentRowId = orderedRowGroups
      .flatMap((g) => g.rows)
      .find((r) => r.cards.some((c) => c.id === sourceCard.id))?.rowId ?? null;
    // 除外・重複排除は buildMoveTargets に閉じ込める (rowId ではなく shiftId で判定する)
    return buildMoveTargets(
      buildMoveTargetRows(orderedRowGroups, { shiftIdOfRow, driverNameById }),
      { currentRowId, sourceShiftId: currentRowId ? shiftIdOfRow(currentRowId) : null },
    );
  }, [detailSelection, orderedRowGroups, deliveries, rowIdToShiftTruckId, driverNameById]);

  // ─────────────────────────────────────────────────────────────
  // 配車サマリの運賃計算。可否の判定は resolveMapTariffAvailability に閉じ込め、
  // ここでは呼ぶだけにする (同じ規則がカード配車側と割れないようにするため)。
  // ─────────────────────────────────────────────────────────────
  const { targetIds: tariffTargetIds, disabledReason: tariffDisabledReason } = useMemo(
    () => resolveMapTariffAvailability({
      deliveries: enrichedDeliveries,
      deliveryDataDisabledReason,
      hasPendingRemoval,
      isResetting: operationBulkResetting.isPending,
      // Task 10: 自動配車が deliveries を書き換えている最中は運賃計算も止める
      isBusy: controller.isBusy,
      // 実行中に押し直せると同じ計算が二重に走る
      isCalculatingTariff: tariffAction.isExecuting,
    }),
    [
      enrichedDeliveries, deliveryDataDisabledReason, hasPendingRemoval,
      operationBulkResetting.isPending, controller.isBusy, tariffAction.isExecuting,
    ],
  );

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: `calc(100vh - ${appBarHeight}px)`, overflow: 'hidden',
    }}>
      {/* タブで画面を見分けられるようにする（V3Plans/Presenter.tsx:1266 と同じ流儀） */}
      <title>配車計画 | マップ (新)</title>
      {/* 内容なりに伸びる。折り返して 2 段になっても下段が押し出されるだけ */}
      <Box sx={{ flexShrink: 0 }}>
        <MapToolbar
          onNavigate={onNavigate}
          startOn={rangeStartOn}
          endOn={rangeEndOn}
          onDateRangeChange={onDateRangeChange}
          onAutoDispatch={handleAutoDispatchClick}
          isAutoDispatchDisabled={isAutoDispatchDisabled}
          autoDispatchTooltip={autoDispatchTooltip}
          showScenarioPlanning={useScenarioPlanning}
          onScenarioDispatch={handleScenarioDispatchClick}
          isScenarioDispatchDisabled={isScenarioDispatchDisabled}
          scenarioDispatchTooltip={scenarioDispatchTooltip}
          displayMetrics={displayMetrics}
          onDisplayMetricsChange={setDisplayMetrics}
          orderAttrFilter={orderAttrFilter}
          onOrderAttrFilterChange={setOrderAttrFilter}
          customInputFieldNames={customInputFieldNames}
          isLicenseReady={isLicenseReady}
        />
      </Box>
      {/* 表示日。期間が 1 日なら DisplayDayTabs 自身が何も描かない */}
      <Box sx={{ flexShrink: 0 }}>
        <DisplayDayTabs
          days={displayDays}
          selectedDay={selectedDay ?? ''}
          onChange={onDisplayDayChange}
        />
      </Box>
      {/* minHeight: 0 が無いと flex item の既定 min-height: auto で内容がはみ出し、
          子の overflowY: auto が効かなくなる */}
      <Box ref={paneContainerRef} sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        <Box data-testid="map-left-panel" sx={{ width: fittedLeftPanelWidth, flexShrink: 0, overflowY: 'auto' }}>
        <ConvenienceDispatchVehicleList
          canSortTruckDisplayOrder={canSortTruckDisplayOrder}
          isMutatingTruckOrder={truckDisplayOrderMutation.isPending}
          onTruckOrderChange={handleTruckOrderChange}
          rowGroups={visibleRowGroups}
          onTruckEdit={vehicleAssignmentHandlers.onTruckEdit}
          onDriverEdit={vehicleAssignmentHandlers.onDriverEdit}
          onPlateNumberEdit={vehicleAssignmentHandlers.onPlateNumberEdit}
          isLoading={isLoading}
          displayMetrics={vehicleDisplayMetricSet}
          grossProfitByTruckId={grossProfitByTruckId}
          constraintAlertsByRowId={constraintAlertsByRowId}
          peakLoadByRowId={peakLoadByRowId}
          selectedRowIds={selectedRowIds}
          allSelected={allSelected}
          truckDisplayStatus={truckDisplayStatus}
          onTruckDisplayStatusChange={setTruckDisplayStatus}
          rotations={rotations}
          onRotationsChange={setRotations}
          constraintViolationOnly={constraintViolationOnly}
          onConstraintViolationOnlyChange={setConstraintViolationOnly}
          hasSelection={selectedRowIds.size > 0}
          driverNameById={driverNameById}
          shiftTruckRowInfoById={shiftTruckRowInfoById}
          truckGroupEntitiesWithAll={truckGroupEntitiesWithAll}
          selectedTruckGroupIndices={selectedTruckGroupIndices}
          onSelectedTruckGroupIndicesChange={setSelectedTruckGroupIndices}
          onRowToggle={onRowToggle}
          registerRow={registerRow}
          onSelectAll={onSelectAll}
          onDeselectAll={handleDeselectAll}
          onUnassignSelected={handleUnassignSelected}
          onSwapSelected={handleSwapSelected}
          onDeleteShift={requestDeleteShift}
          deleteShiftDisabledReason={busyDisabledReason}
          onAddVehicle={openAddVehicleDialog}
          onOpenPrint={reportPrinting.open}
          operationStatusText={operationStatusText}
          // 新しい更新があるときだけ渡す。平時は押す用の無いボタンを出さない
          onRefreshOperationStatuses={
            useDriverApp && operationStatus.agoMinutes > 0 ? operationStatus.refresh : undefined
          }
          onSendOperationMail={mailAvailability.visible ? handleSendOperationMail : undefined}
          sendOperationMailDisabledReason={mailAvailability.disabled ? mailAvailability.tooltip : undefined}
          rotationEditable
          onAddRotation={handleAddRotation}
          onDeleteRotation={handleDeleteRotation}
          fixedShiftTruckIds={fixedShiftTruckIds}
          onToggleFixedSelected={handleToggleFixedSelected}
          fixedToggleLabel={fixedToggleLabel}
          isTogglingFixed={fixedStatusMutation.isPending}
          fixedToggleDisabledReason={busyDisabledReason}
          deleteRoundDisabledReason={busyDisabledReason}
          vehicleColorFor={vehicleColorFor}
          onVehicleColorClick={onVehicleColorClick}
          colorDisabledReason={busyDisabledReason}
          rowExpandable
          expandedRowIds={expandedRowIds}
          onToggleRowExpanded={onToggleRowExpanded}
          onToggleAllExpanded={onToggleAllExpanded}
          allRowsExpanded={isAllRowsExpanded}
          expandedRowVMByRowId={expandedRowVMByRowId}
          onSelectStop={handleSelectStop}
          onReorderStops={handleReorderStops}
          onReorderRotations={handleReorderRotations}
          reorderDisabledReason={busyDisabledReason}
          screenName={SCREEN_NAMES.PLANNING_MAP}
          newlyAssignedByShiftTruck={newlyAssignedByShiftTruck}
        />
      </Box>

      <PanelResizeHandle
        side="left"
        width={fittedLeftPanelWidth}
        defaultWidth={DEFAULT_LEFT_PANEL_WIDTH}
        label="左リストの幅を調整"
        screenName={SCREEN_NAMES.PLANNING_MAP}
        onWidthChange={setLeftPanelWidth}
        containerWidth={paneContainerWidth}
        reservedWidth={reservedWidthFor(rightPanelOpen ? fittedRightPanelWidth : 0, rightPanelOpen)}
      />

      {/* position: relative は詳細パネル (placement='mapTopRight') の絶対配置の基準。
          これが無いと top/right が画面基準になり、maxHeight: calc(100% - 32px) も
          地図ペインの高さで止まらない。高さは flex の stretch で確定している */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {/* 表示フィルタは地図の右上に重ねる（見ている地図の上で切り替えられるように）。
            詳細パネルは top: 44 に下げてあるので縦に並ぶ */}
        <MapDisplayFilter
          displayKinds={displayKinds}
          onDisplayKindsChange={setDisplayKinds}
          showTruckStatus={showTruckStatus}
          onShowTruckStatusChange={useTruckPosition ? setShowTruckStatus : undefined}
        />
        <MapView
          routes={routes}
          unassignedMarkers={unassignedMarkers}
          unassignedTooltips={unassignedTooltips}
          unassignedPolylines={unassignedPolylines}
          rightPanelOpen={rightPanelOpen}
          leftPanelWidth={fittedLeftPanelWidth}
          rightPanelWidth={fittedRightPanelWidth}
          displayKinds={displayKinds}
          showTruckStatus={showTruckStatus}
          startOn={startOn}
          endOn={endOn}
          garages={garageEntities}
          selectedRowIds={selectedRowIds}
          selectedUnassignedIds={selectedUnassignedIds}
          onMarkerRowClick={onMarkerRowClick}
          onMarkerClick={handleMarkerClick}
          onUnassignedClick={handleUnassignedClick}
          onMarkerRowDeselect={onMarkerRowDeselect}
          onUnassignedDeselect={onUnassignedDeselect}
          onBackgroundClick={handleMapBackgroundClick}
          isLoading={isLoading}
        />
        {detailOpen && detailSelection && detailPanelProps && (
          <DetailPanelShell
            selection={detailSelection}
            placement="mapTopRight"
            screenName={detailActions.screenName}
            hasDuplicate={detailPanelProps.hasDuplicate}
            geoAlert={detailPanelProps.geoAlert}
            onGeoUpdate={detailActions.handleGeoUpdate}
            moveMenuAnchor={moveMenuAnchor}
            onChangeMoveMenuAnchor={setMoveMenuAnchor}
            onClose={closeDetail}
            onEditOrder={detailActions.handleEditUnassignedOrder}
            onEditLane={detailActions.handleEditLane}
            onMoveToVehicle={detailActions.handleMoveToVehicle}
            onResetToUnassigned={detailActions.handleResetToUnassigned}
            onSplit={detailActions.handleOpenSplitDialog}
            onDelete={detailActions.handleDeleteUnassignedFromDetail}
            onCopy={detailActions.handleCopyUnassignedFromDetail}
            moveTargets={moveTargets}
            resolveShipmentOrderId={detailActions.resolveShipmentOrderId}
            disabledReason={busyDisabledReason}
          />
        )}
        {sharedDestinationVM && (
          <SharedDestinationPanel
            vm={sharedDestinationVM}
            screenName={SCREEN_NAMES.PLANNING_MAP}
            onSelectEntry={handleSelectSharedEntry}
            onClose={() => setSharedDestPlaceKey(null)}
          />
        )}
      </Box>

      {/* 畳んでいる間は出さない。幅 0 のパネルを掴めてしまう */}
      {rightPanelOpen && (
        <PanelResizeHandle
          side="right"
          width={fittedRightPanelWidth}
          defaultWidth={DEFAULT_RIGHT_PANEL_WIDTH}
          label="右リストの幅を調整"
          screenName={SCREEN_NAMES.PLANNING_MAP}
          onWidthChange={setRightPanelWidth}
          onResizingChange={setRightPanelResizing}
          containerWidth={paneContainerWidth}
          reservedWidth={reservedWidthFor(fittedLeftPanelWidth)}
        />
      )}

      {/* 右ペインは畳める（サンプル MapPage.tsx:2352-2382 の rightPanelOpen）。
          閉じても中身はマウントしたままにする。アンマウントすると選択・絞り込み・
          表示単位が初期化され、開き直したときに元へ戻らない */}
      <Box sx={{ position: 'relative', display: 'flex', height: '100%', minHeight: 0, flexShrink: 0 }}>
        <Box
          component="button"
          type="button"
          onClick={toggleRightPanel}
          aria-label={rightPanelOpen ? '右パネルを閉じる' : '右パネルを開く'}
          sx={{
            // 地図側へ 12px はみ出させて、閉じているときも同じ位置から開ける。
            // zIndex は leaflet のコントロール (最大 1000) より上
            position: 'absolute',
            left: -12,
            top: 12,
            zIndex: 1100,
            display: 'flex',
            height: 24,
            width: 24,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            border: '1px solid hsl(var(--border))',
            bgcolor: 'hsl(var(--background))',
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            cursor: 'pointer',
            p: 0,
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'hsl(var(--secondary))' },
          }}
        >
          <ChevronRightRounded
            sx={{
              fontSize: 16,
              color: 'hsl(var(--muted-foreground))',
              transition: 'transform 0.2s',
              transform: rightPanelOpen ? 'rotate(0deg)' : 'rotate(180deg)',
            }}
          />
        </Box>
        <Box
          data-testid="map-right-panel"
          sx={{
            width: rightPanelOpen ? fittedRightPanelWidth : 0,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            transition: rightPanelResizing ? 'none' : 'width 0.2s',
          }}
        >
        {/* 表示項目トグルはヘッダのツールバーへ移した（サンプル 7.png の 1 行に集約する指示）。
            **1 箇所にだけ置く**ので、ここに復活させないこと
            （MapToolbar.test.tsx が「1 つだけある」ことを固定している）。 */}
        {/* 外側は畳むときに 0 まで縮むので、中身の幅はここで持つ（縮む間に中が組み直されない） */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', width: fittedRightPanelWidth }}>
          <ConvenienceDispatchUnassignedList
            onDeleteSelected={handleDeleteSelectedUnassigned}
            readOnly
            displayModeSwitchable
            items={visibleUnassignedItems}
            rowGroups={orderedRowGroups}
            orderAttrFilter={orderAttrFilter}
            deliveries={enrichedDeliveries}
            isLoading={isLoading}
            displayMetrics={displayMetricSet}
            selectedItemIds={selectedUnassignedIds}
            onSelectItem={onSelectUnassigned}
            onShowDetail={handleShowUnassignedDetail}
            onRestoreSplit={handleRestoreSplit}
            restoreSplitDisabledReason={busyDisabledReason}
            screenName={SCREEN_NAMES.PLANNING_MAP}
            deliveryListGrouping={deliveryListGrouping}
            customInputFieldNames={customInputFieldNames}
            trucksForFilter={trucksForFilter}
            driversForFilter={driversForFilter}
            companyId={companyId}
            isLicenseReady={isLicenseReady}
            isMastersReady={isMastersReady}
            isMastersError={isMastersError}
            onOpenTariffCalculation={openTariffDialog}
            tariffCalculationDisabledReason={tariffDisabledReason ?? undefined}
            notAllocReasonsByOrderId={notAllocReasonsByOrderId}
            notAllocDeliveries={enrichedDeliveries as unknown as PlanningsDeliveryEntity[]}
            notAllocTrucks={trucks}
            notAllocDrivers={drivers}
            onScenarioFilterChange={setScenarioFilter}
          />
        </Box>
        </Box>
      </Box>
      </Box>

      {colorAnchor && (() => {
        const t = truckById.get(colorAnchor.truckId);
        return (
          <VehicleColorPickerPopover
            anchorEl={colorAnchor.el}
            truckId={colorAnchor.truckId}
            currentTruckColor={t?.color ?? null}
            partnerCompanyId={t?.partnerCompanyId ?? undefined}
            currentPartnerCompanyColor={t?.partnerCompanyColor ?? null}
            truckName={truckNameByTruckId.get(colorAnchor.truckId) ?? ''}
            partnerCompanyName={t?.partnerCompanyName ?? null}
            onClose={() => setColorAnchor(null)}
            getBusyGuardReason={getBusyGuardReason}
          />
        );
      })()}

      <DispatchDetailDialogs
        laneSheetId={detailActions.laneSheetId}
        onCloseLaneSheet={detailActions.closeLaneSheet}
        editShipmentOrderId={detailActions.editShipmentOrderId}
        onCloseEditShipmentOrder={detailActions.closeEditShipmentOrder}
        startOn={startOn}
        splitDialogOpen={detailActions.splitDialogOpen}
        splitTargetLegRun={detailActions.splitTargetLegRun}
        onCloseSplitDialog={detailActions.closeSplitDialog}
        onSplitSuccess={detailActions.handleSplitSuccess}
        getBusyGuardReason={getBusyGuardReason}
        disabledReason={busyDisabledReason}
      />

      <TariffCalculationDialog
        dialogIsOpen={tariffDialogOpen}
        setDialogIsOpen={setTariffDialogOpen}
        selectedDeliveryIds={tariffTargetIds}
        targetDescription={<>対象期間の配車済みルート<strong>全{tariffTargetIds.length}件</strong>について、運賃を再計算して保存します。</>}
        // 進行と結果は右下のトースト（自動配車と同じ場所）で知らせるので、完了を待たずに閉じる
        onExecute={(params) => { setTariffDialogOpen(false); tariffAction.execute(params); }}
        isExecuting={tariffAction.isExecuting}
      />

      {/* 車両 / 車番 / ドライバーの変更 UI。カード配車と同一実体 */}
      <VehicleAssignmentMenus {...vehicleAssignmentMenuProps} />

      {/* 車両追加・帳票出力ともカード配車と同一実体。open は各々が自前で持つ */}
      <ConvenienceDispatchAddVehicleDialog
        open={addVehicleDialogOpen}
        onClose={() => setAddVehicleDialogOpen(false)}
        startOn={startOn}
        endOn={endOn}
        existingTruckIds={existingTruckIds}
        existingDriverIds={existingDriverIds}
        trucks={trucks}
        drivers={drivers}
      />
      <ReportPrintingDialogs {...reportPrinting.dialogProps} />

      <ConfirmDialog
        open={confirmDialog !== null}
        title={confirmDialog?.title}
        message={confirmDialog?.description ?? ''}
        handleCancel={() => setConfirmDialog(null)}
        handleOk={() => {
          const cb = confirmDialog?.onConfirm;
          setConfirmDialog(null);
          if (cb) void cb();
        }}
      />

      <Dialog
        open={planningDialogOpen}
        onClose={handlePlanningDialogClose}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { maxWidth: 720 } }}
      >
        <PlanningPresenter
          screenName={SCREEN_NAMES.PLANNING_MAP}
          {...planningDialogSettingProps}
          planningRequest={handlePlanningRequest}
          onClose={handlePlanningDialogClose}
          selectedDeliveryIds={selectedShiftIds}
          selectedOrderIds={selectedOrderIds}
          startOn={startOn}
          relaxedRules={relaxedRules}
          selectedRelaxedRules={selectedRelaxedRules}
          setSelectedRelaxedRules={setSelectedRelaxedRules}
          setPriorityLargeTrucks={setPriorityLargeTrucks}
          priorityLargeTrucks={priorityLargeTrucks}
          onPlanningConditionsChange={setPlanningConditions}
        />
      </Dialog>

      {/* ScenarioPlanningListDialog 自身が内部に Dialog を持つ (dialogIsOpen が false の間は
          null を返す)。V3Plans/Presenter.tsx の scenarioDialogMemo と同じ配置規則で
          Dialog によるラップはしない。customInputFields は /map の customInputFieldNames を
          そのまま流用する (両者とも「32 列フィルタへ素通しする値」で意味が同じことを実物で確認済み) */}
      <ScenarioPlanningListDialog
        dialogIsOpen={scenarioDialogOpen}
        setDialogIsOpen={setScenarioDialogOpen}
        customInputFields={customInputFieldNames}
        requestScenarioPlanning={requestScenarioPlanning}
      />

      {/* 自動配車 / 未割当に戻す 実行中は右下にトーストでフィードバック (設計書 :454-461)。
          /map は全ページ取得で数秒〜数十秒かかり、enqueueSnackbar が消えたあとに
          「実行中である」表示が無くなるため、実行中ずっと出続ける常設要素で埋める。
          /map には固定ルート適用チェーンが無い (:205 のコメント参照) ため、カード配車にある
          `!applyChainBusy` 相当の抑止は無い。 */}
      {/* 未割当が出たときの「配車のヒント」。カード配車と同じコンポーネントを使う */}
      <NotAllocSuggestionComponent
        showNotAllocSuggestionSnackbar={showNotAllocSuggestionSnackbar}
        suggestionDialogOpen={suggestionDialogOpen}
        notAllocSuggestions={notAllocSuggestions}
        handleSuggestionDialogClose={handleSuggestionDialogClose}
        setShowNotAllocSuggestionSnackbar={setShowNotAllocSuggestionSnackbar}
        setSuggestionDialogOpen={setSuggestionDialogOpen}
      />

      {/* サーバが受け付けてから出す。POST が返る前は planning がまだ作られておらず、
          「計算中」と言いながら止める対象が無い状態になる
          (旧地図配車も queued を確認してから出す。V3Plans/Component.tsx:952) */}
      {progressToastLabel && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: (theme) => theme.zIndex.snackbar + 10,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            boxShadow: '0 10px 30px -10px rgba(0,0,0,0.4)',
            minWidth: 240,
          }}
        >
          <CircularProgress size={20} thickness={4} color="primary" />
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {progressToastLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              しばらくお待ちください
            </Typography>
          </Box>
          {/* 実行を止める手段が無かった。V3Plans の「実行を停止する」
              (V3Plans/Presenter.tsx:1026-1039) と同じく、確認してから
              controller.cancel（= running の DELETE）を撃つ。
              未割当に戻す処理は API が 1 本で途中で止められないので出さない */}
          {/* このトーストは isAccepted のときだけ出るので、ここでは
              「未割当に戻す最中か」だけを見れば足りる */}
          {!operationBulkResetting.isPending && (
            <Tooltip title="自動配車の実行を停止する" arrow>
              <IconButton
                size="small"
                aria-label="自動配車の実行を停止する"
                onClick={() => {
                  ReactGA.event('click', {
                    screen_name: SCREEN_NAMES.PLANNING_MAP,
                    button_name: '自動配車 停止',
                  });
                  // 文言は V3Plans/Presenter.tsx:1027 と同一にする
                  setConfirmDialog({
                    description: '実行中の自動配車を停止します。よろしいですか？',
                    // ユーザーが押した停止。止められなかったときは黙って捨てず伝える
                    onConfirm: () => { controller.cancel({ notifyOnFailure: true }); },
                  });
                }}
                sx={{ ml: 'auto', alignSelf: 'flex-start', mt: -0.5, mr: -0.5 }}
              >
                <CloseRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MapPagePresenter;
